import * as React from "react";
import { Link } from "react-router-dom";
import Layout from "@components/Layout";
import { Card, CardContent } from "@components/ui/card";
import { HelionButton } from "@components/ui/HelionButton";
import { API_BASE_URL } from "@lib/config";
import { parsePeakFreshness, peakFreshnessView, type PeakFreshness } from "@lib/peakFreshness";
import { useUser } from "../hooks/useUser";
import { useSignalR } from "../hooks/useSignalR";
import { fmtLocalDateTime, parseUtc } from "@lib/dateUtils";
import { PeriodReturnTile } from "@components/cockpit/PeriodReturnTile";
import { sessionsBehind, sessionStale } from "@components/cockpit/freshness";
import { OwedActionPanel, type OwedActionRow } from "@components/cockpit/OwedActionPanel";
import { SectionDrawer, SectionRail, useDrawerState } from "@components/cockpit/SectionDrawer";
import {
  PORTFOLIO_SECTIONS,
  assertDrawerInvariant,
  buildDrawerSpecs,
} from "@lib/portfolioSections";
import { PortfolioMethodologyModal } from "@components/PortfolioMethodologyModal";
import { buildWatchlist } from "@lib/exitWatchlist";
import type { NavReconciliation } from "@lib/navTypes";
import { successNavNote, syncSuccessMessage, type RefreshPortfolioResponse } from "@lib/refreshPortfolio";

import LedgerConfirmCard from "../components/LedgerConfirmCard";
import NavIntradayChart, {
  NavRangeChart,
  type NavIntradaySeries,
  type NavRangeSeries,
} from "../components/NavIntradayChart";
import { DataTable } from "../components/ui/data-table";
import {
  heldColumns,
  dollars,
  pct,
  toYMD,
  gammaPer1Pct,
  type HeldLeapRow,
  type FixedIncomeRow,
} from "../components/ui/portfolio-columns";
import { resolvePortfolioTrimState } from "../lib/portfolioTrimState";

/* ----------------------- theme-safe UI helpers ----------------------- */
const ui = {
  pageBg: "var(--bg-primary)",
  cardBg: "var(--card-bg)",
  cardBorder: "var(--card-border)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textMuted: "var(--text-muted)",
  shadow: "var(--shadow-soft)",
  rowAlt: "rgba(148, 163, 184, 0.08)",
  rowHeader: "rgba(148, 163, 184, 0.12)",
  chipBg: "rgba(148, 163, 184, 0.16)",
  accentInfo: "var(--accent-info)",
  barBg: "rgba(148, 163, 184, 0.18)",
};

type Tone = "neutral" | "info" | "success" | "warning" | "danger";

const toneStyles: Record<Tone, React.CSSProperties> = {
  neutral: {
    backgroundColor: "rgba(148, 163, 184, 0.14)",
    border: "1px solid rgba(148, 163, 184, 0.24)",
    color: ui.textPrimary,
  },
  info: {
    backgroundColor: "rgba(59, 130, 246, 0.14)",
    border: "1px solid rgba(59, 130, 246, 0.28)",
    color: ui.textPrimary,
  },
  success: {
    backgroundColor: "rgba(16, 185, 129, 0.14)",
    border: "1px solid rgba(16, 185, 129, 0.28)",
    color: ui.textPrimary,
  },
  warning: {
    backgroundColor: "rgba(245, 158, 11, 0.14)",
    border: "1px solid rgba(245, 158, 11, 0.28)",
    color: ui.textPrimary,
  },
  danger: {
    backgroundColor: "rgba(239, 68, 68, 0.14)",
    border: "1px solid rgba(239, 68, 68, 0.28)",
    color: ui.textPrimary,
  },
};

const themeCardStyle: React.CSSProperties = {
  backgroundColor: ui.cardBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
  boxShadow: ui.shadow,
};

const sectionCardStyle: React.CSSProperties = {
  backgroundColor: ui.rowAlt,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
};

const inlineBoxStyle: React.CSSProperties = {
  backgroundColor: ui.cardBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
};

const smallMutedText: React.CSSProperties = {
  color: ui.textMuted,
};

const secondaryText: React.CSSProperties = {
  color: ui.textSecondary,
};

const metricPositiveText: React.CSSProperties = {
  color: "rgb(22, 163, 74)",
};

const metricNegativeText: React.CSSProperties = {
  color: "rgb(220, 38, 38)",
};

function badgeStyle(tone: Tone): React.CSSProperties {
  return {
    ...toneStyles[tone],
    borderRadius: 8,
    padding: "2px 8px",
  };
}

/* ----------------------- helpers: robust numeric coercion ----------------------- */
const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toInt = (v: any): number | null => {
  const n = toNum(v);
  return n === null ? null : Math.trunc(n);
};

/**
 * Normalize pct-like inputs to decimals:
 * - If value is 0..1 => already decimal
 * - If value is 1..100 => treat as percent and divide by 100 (defensive)
 * - If null/invalid => null
 */
const toPctDecimal = (v: any): number | null => {
  const n = toNum(v);
  if (n === null) return null;
  if (!Number.isFinite(n)) return null;
  if (n > 1.5 && n <= 100) return n / 100;
  return n;
};

/* -------------------- UI-only derived signal helpers (safe) -------------------- */
const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

/* ---- Trading-session-aware staleness (US Eastern, DST-correct) ----
 * EOD/per-session feeds (option prices, greeks, NAV) update once per market
 * session, so age is measured in trading sessions behind: Friday's data viewed
 * Monday is 1 session behind, not stale.
 *
 * sessionsBehind/sessionStale now come from the shared cockpit module instead of
 * the local copies this file carried since 2026-06-16 (the "shared-util extraction
 * is deferred" note). Verified line-by-line identical before the swap, so no
 * displayed staleness state can change. Only the LABEL stays local: the shared
 * helper says "N sessions ago" and cannot take null, while this page says
 * "N sessions behind" and must render "unknown" when the snapshot time is absent. */
function sessionAgeLabel(ms: number | null): string {
  if (ms === null) return "unknown";
  const n = sessionsBehind(ms, Date.now());
  if (n <= 0) return "current";
  return n === 1 ? "1 session behind" : `${n} sessions behind`;
}

// "AT CAP" and "OVER TARGET" are produced at the executable-trim resolver and
// rendered in the resting line; the union predated them and only compiled because
// React's missing types made the prop `any` (found 2026-09-02).
type ActionState = "HOLD" | "TRIM" | "ADD" | "AT CAP" | "OVER TARGET";

/**
 * UI tolerance (display-only) should align to your rebalance engine tolerance band.
 * You asked for ~4% (0.04).
 */
const UI_REBALANCE_TOLERANCE = 0.04;

function resolveAllocationToleranceBand(targetPct: number | null, tolerancePct?: number | null): number {
  const relTol =
    typeof tolerancePct === "number" && isFinite(tolerancePct)
      ? tolerancePct
      : UI_REBALANCE_TOLERANCE;

  if (typeof targetPct !== "number" || !isFinite(targetPct)) {
    return relTol;
  }

  return Math.abs(targetPct) * relTol;
}

function formatAllocationTolerance(targetPct: number | null, tolerancePct?: number | null): string {
  const relTol =
    typeof tolerancePct === "number" && isFinite(tolerancePct)
      ? tolerancePct
      : UI_REBALANCE_TOLERANCE;
  const absTol = resolveAllocationToleranceBand(targetPct, tolerancePct);
  return `${(relTol * 100).toFixed(0)}% of target (~${(absTol * 10000).toFixed(0)} bps)`;
}

function computeAllocationAction(args: {
  targetPct: number | null;
  currentPct: number | null;
  portfolioNav: number | null;
  tolerancePct?: number | null;
}) {
  const { targetPct, currentPct, portfolioNav, tolerancePct } = args;

  if (
    typeof targetPct !== "number" ||
    !isFinite(targetPct) ||
    typeof currentPct !== "number" ||
    !isFinite(currentPct)
  ) {
    return {
      state: "HOLD" as ActionState,
      reason: "Allocation telemetry missing",
      overPctPts: null as number | null,
      trimNotional: null as number | null,
      targetPct,
      currentPct,
    };
  }

  const delta = currentPct - targetPct;
  const overPctPts = delta * 100;
  const tol = resolveAllocationToleranceBand(targetPct, tolerancePct);

  if (delta > tol) {
    const trimNotional =
      typeof portfolioNav === "number" && isFinite(portfolioNav) ? delta * portfolioNav : null;

    return {
      state: "TRIM" as ActionState,
      reason: `Over target (${formatAllocationTolerance(targetPct, tolerancePct)} band)`,
      overPctPts,
      trimNotional,
      targetPct,
      currentPct,
    };
  }

  if (delta < -tol) {
    return {
      state: "ADD" as ActionState,
      reason: `Under target (${formatAllocationTolerance(targetPct, tolerancePct)} band)`,
      overPctPts,
      trimNotional: null,
      targetPct,
      currentPct,
    };
  }

  return {
    state: "HOLD" as ActionState,
    reason: `Within ${formatAllocationTolerance(targetPct, tolerancePct)} tolerance`,
    overPctPts,
    trimNotional: null,
    targetPct,
    currentPct,
  };
}


/* ----------------------------- Types + normalizer ------------------------------ */
interface Overview {
  portfolioNAV: number;
  vixZScore: number | null;
  vixCurrent?: number | null;
  leapsTargetPct: number;
  currentLeapsPct: number;
  remainingNotionalToDeploy: number;
  lastRefreshedUTC?: string;
  pending_harvest?: boolean;
  harvest_window?: boolean;
  reversion_confirm_days?: number;
  harvest_config?: Record<string, any>;
  early_defense?: {
    enabled?: boolean;
    active?: boolean;
    target_pct?: number | null;
    reason?: string | null;
    conditions?: Record<string, any>;
  } | null;
}

type CashOverrideState = {
  enabled?: boolean | null;
  observed_cash?: number | null;
  reserved_cash?: number | null;
  deployable_cash?: number | null;
  deployed_capital?: number | null;
  deployment_pct?: number | null;
  mode?: string | null;
  reason?: string | null;
} | null;

type LatestNavState = {
  total_nav?: number | null;
  cash_nav?: number | null;
  as_of?: string | null;
  source?: string | null;
  cash_override?: CashOverrideState;
  // Typed against the shared declaration (2026-08-10) rather than `any`: the estimated
  // period return renders from this block here as well as on Risk and Signals, and an
  // untyped read is how the same figure ends up computed from a renamed field on one page.
  reconciliation?: NavReconciliation | null;
} | null;

// Lifted out of fetchLatestNav so the HTTP load AND the SignalR `nav` push produce an
// identical LatestNavState (carrying the full reconciliation block). Pure function of
// the wire payload — no component state.
function normalizeNavPayload(json: any): LatestNavState {
  const navObj = json?.nav ?? null;
  const cashOverride = json?.cashOverride ?? json?.cash_override ?? null;
  return {
    reconciliation: json?.navReconciliation ?? null,
    total_nav:
      typeof navObj?.TotalNAV === "number" ? navObj.TotalNAV : typeof json?.total_nav === "number" ? json.total_nav : null,
    cash_nav:
      typeof navObj?.CashNAV === "number" ? navObj.CashNAV : typeof json?.cash_nav === "number" ? json.cash_nav : null,
    as_of:
      typeof json?.asOfUTC === "string" ? json.asOfUTC : typeof navObj?.AsOfDate === "string" ? navObj.AsOfDate : typeof json?.as_of === "string" ? json.as_of : null,
    source: typeof json?.source === "string" ? json.source : null,
    cash_override:
      cashOverride && typeof cashOverride === "object"
        ? {
            enabled: typeof cashOverride?.enabled === "boolean" ? cashOverride.enabled : null,
            observed_cash:
              typeof cashOverride?.observed_cash === "number" ? cashOverride.observed_cash
                : typeof cashOverride?.observedCash === "number" ? cashOverride.observedCash
                : typeof navObj?.CashNAV === "number" ? navObj.CashNAV : null,
            reserved_cash:
              typeof cashOverride?.reserved_cash === "number" ? cashOverride.reserved_cash
                : typeof cashOverride?.reservedCash === "number" ? cashOverride.reservedCash
                : null,
            deployable_cash:
              typeof cashOverride?.deployable_cash === "number" ? cashOverride.deployable_cash
                : typeof cashOverride?.deployableCash === "number" ? cashOverride.deployableCash
                : null,
            deployed_capital:
              typeof cashOverride?.deployed_capital === "number" ? cashOverride.deployed_capital
                : typeof cashOverride?.deployedCapital === "number" ? cashOverride.deployedCapital
                : null,
            deployment_pct:
              typeof cashOverride?.deployment_pct === "number" ? cashOverride.deployment_pct
                : typeof cashOverride?.deploymentPct === "number" ? cashOverride.deploymentPct
                : null,
            mode: typeof cashOverride?.mode === "string" ? cashOverride.mode : null,
            reason: typeof cashOverride?.reason === "string" ? cashOverride.reason : null,
          }
        : null,
  } as LatestNavState;
}

type RebalanceMeta = {
  regime?: string | null;
  effective_regime?: string | null;
  phase?: any;
  vix_zscore?: number | null;
  vix_zscore_yesterday?: number | null;
  delta_z?: number | null;
  vix_current?: number | null;
  vix_delta?: number | null;
  live_vix_used?: boolean;

  funded_weight?: number | null;
  target_pct_raw?: number | null;
  target_pct_capped?: number | null;
  target_pct_effective?: number | null;
  target_source?: string | null;
  rebalance_tolerance_pct?: number | null;
  vix_trim_enabled?: boolean;

  leaps_nav?: number | null;
  portfolio_nav?: number | null;
  current_weight?: number | null;

  desired_notional?: number | null;
  trim_notional?: number | null;
  hard_cap_trim?: number | null;
  suggested_trim_to_target?: number | null;
  // WHICH PATH produced this rebalance meta. "fallback" means the real engine could
  // not run (weekend, holiday, stale-feed block) and NOTHING was evaluated -- so an
  // absent trim means "we did not look", not "the engine says hold". (backend 47e3d1b3)
  rebalance_source?: string | null;
  rebalance_engine_available?: boolean | null;
  rebalance_note?: string | null;
  // Market weight above the FUNDED cap is expected (57.8% of days on the 2013-2026
  // baseline) and is NOT a breach -- surfaced as context so it is never read as one.
  market_weight_over_funded_cap?: boolean | null;
  trim_reason?: string | null;

  pending_harvest?: boolean;
  harvest_window?: boolean;
  reversion_confirm_days?: number;

  defense_by_z?: boolean;
  defense_confirmed?: boolean;
  block_reason?: string | null;
  cooldown_active?: boolean;

  caps?: {
    funded_max?: number | null;
    growth_max?: number | null;
  } | null;

  early_defense?: {
    enabled?: boolean;
    active?: boolean;
    target_pct?: number | null;
    reason?: string | null;
    conditions?: Record<string, any>;
  } | null;

  spx?: Record<string, any> | null;
  term?: Record<string, any> | null;
} | null;

type RebalanceOrderPreview = {
  type?: "SELL" | "BUY" | string;
  contract_id?: string | number;
  contractId?: string | number;
  estimated_qty?: number | string;
  estimatedQty?: number | string;
  target_notional?: number | string;
  targetNotional?: number | string;
  reason?: string | null;
  display_name?: string | null;
  display?: string | null;
  symbol?: string | null;
  right?: string | null;
  strike?: number | null;
  expiration?: string | null;
  contractDesc?: string | null;
} | null;

type PendingOrderRow = {
  orderId?: number;
  action?: string;
  contractId?: string;
  displayName?: string | null;
  symbol?: string | null;
  right?: string | null;
  strike?: number | null;
  expiration?: string | null;
  contractDesc?: string | null;
  scheduledDate?: string | null;
  estimatedQty?: number | null;
  targetNotional?: number | null;
  overrideQty?: number | null;
  overrideNotional?: number | null;
  targetAllocation?: number | null;
  currentAllocation?: number | null;
  reason?: string | null;
  status?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  approvedAt?: string | null;
  mlScore?: number | null;
  mlPercentile?: number | null;
  tags?: string | null;
};

type HomeRunCandidate = {
  contractId?: string;
  symbol?: string | null;
  accountId?: string | null;
  displayName?: string | null;
  quantity?: number | null;
  entryPrice?: number | null;
  marketPrice?: number | null;
  marketValue?: number | null;
  returnPct?: number | null;
  returnMultiple?: number | null;
  triggerMultiple?: number | null;
  trimPct?: number | null;
  suggestedTrimQty?: number | null;
  suggestedRunnerQty?: number | null;
  suggestedHarvestNotional?: number | null;
  canPartialTrim?: boolean;
  singleContractConstraint?: boolean;
  expiration?: string | null;
  dte?: number | null;

  // Backend aliases used by /api/home_run_harvest. Keep both shapes so the UI
  // does not silently hide or disable valid 2x candidates if payload naming changes.
  recommendedTrimQty?: number | null;
  estimatedHarvestNotional?: number | null;
  runnerQtyAfterTrim?: number | null;
  harvestable?: boolean;
  warning?: string | null;
};

type HomeRunLock = {
  lockId?: number;
  contractId?: string;
  symbol?: string | null;
  accountId?: string | null;
  displayName?: string | null;
  trimQuantity?: number | null;
  runnerQuantity?: number | null;
  harvestedNotional?: number | null;
  executedAtUTC?: string | null;
  unlockAtUTC?: string | null;
  daysUntilUnlock?: number | null;
  status?: string | null;
};

type HomeRunPayload = {
  policy?: Record<string, any>;
  summary?: {
    activeLockCount?: number;
    eligibleUnlockCount?: number;
    lockedNotional?: number;
    lockedPctNav?: number | null;
    nextUnlockAtUTC?: string | null;
    candidateCount?: number;
    candidateHarvestNotional?: number;
    singleContractCandidateCount?: number;
  };
  activeLocks?: HomeRunLock[];
  eligibleUnlocks?: HomeRunLock[];
  recentLocks?: HomeRunLock[];
  candidates?: HomeRunCandidate[];
};

/** Counts behind the "can I trust the number above" strip.
 *
 * Computed from the RAW get_portfolio_view rows, not the mapped HeldLeapRow[]: the
 * mapper drops risk_source, risk_updated_at_utc and the un-COALESCEd bid/ask, which
 * are exactly the fields that say whether a figure is real or a stored leftover.
 *
 * Everything here is a COUNT of lines with a problem. Deliberately no percentages
 * and no "coverage" figure — see the note on noTwoSidedQuote. */
interface BookIntegrity {
  lines: number;
  /** OLDEST non-null risk snapshot, not the newest. The page's headline freshness is a
   *  max() across rows, so one freshly-quoted line makes a stale book read "current";
   *  worse, risk_updated_at_utc is NULL by construction for every row whose risk_source
   *  is 'held_leaps' or 'missing', so the least-fresh lines are precisely the ones a
   *  max() cannot see. */
  oldestRiskMs: number | null;
  /** Lines carrying no risk snapshot stamp at all. */
  noRiskStamp: number;
  /** Lines without BOTH a bid and an ask. Not expressed as "quote coverage %": the
   *  backend's Mid COALESCE chain terminates at h.market_price, so a stored mark makes
   *  mid non-null for essentially every row — a coverage percentage built on it would
   *  read ~100% on a book with no live quotes at all. */
  noTwoSidedQuote: number;
  /** risk_source === 'missing' — the backend's own marker for "no greeks on this line". */
  noGreeks: number;
  /** Lines with no entry price. Both cost paths coerce a null entry to $0, which
   *  deflates cost basis AND the funded-basis % measured against the 40% cap — every
   *  error in the reassuring direction. */
  noEntryPrice: number;
  noEntryPriceMv: number;
}

interface PortfolioPayload {
  overview: Overview;
  /** Per-stage build warnings get_portfolio_view already emits and nothing has read. */
  warnings?: string[];
  integrity?: BookIntegrity;
  heldLeaps: HeldLeapRow[];
  fixedIncome: FixedIncomeRow[];
  fixedIncomeTargets?: Record<string, number>;
  rebalance?: RebalanceMeta;
  rebalanceOrdersPreview?: RebalanceOrderPreview[];
  pendingOrders?: PendingOrderRow[];
  homeRun?: HomeRunPayload;
}

type HeldLeapRowX = HeldLeapRow & {
  updatedAt?: string | null;
  singleContractConstraint?: boolean;
  minContractsForTrim?: number;
  /** false = the hard-exit stack was NOT evaluated for this row — render as unknown,
   *  never as clear. (Surfaced by the @types/react install, 2026-09-02: this and the two
   *  fields above were read off a plainly-typed row and only compiled because React's
   *  missing type declarations made every component prop `any`.) */
  hardExitEvaluated?: boolean;
};

type RawHeldLeapRow = {
  /** Lot-size constraint on the 2x harvest, set by the backend for EVERY held row
   *  from quantity (not only for positions already past the trigger). */
  home_run_single_contract_constraint?: boolean;
  home_run_min_contracts_for_trim?: number | null;
  contract_id?: number | string;
  conid?: number | string | null;
  DisplayName?: string | null;
  display_name?: string | null;
  symbol?: string | null;
  right?: string | null;
  strike?: number | string | null;
  expiration?: string | null;
  quantity?: number | string | null;
  entry_price?: number | string | null;
  market_price?: number | string | null;
  market_value?: number | string | null;
  account_id?: string | null;
  sub_account_id?: string | null;
  dte?: number | string | null;
  peak_return?: number | string | null;
  drawdown_from_peak?: number | string | null;
  likely_peaked?: number | boolean | null;
  exit_hint_reason?: string | null;
  exit_hint_score?: number | string | null;
  exit_hint_percentile?: number | string | null;
  exit_risk_rank_of?: number | null;
  exit_risk_is_rank?: boolean | null;
  UpdatedAt?: string | null;
  return_pct?: number | string | null;
  account_class?: "MASTER" | "SUB" | "UNKNOWN" | null;
  age_days?: number | string | null;
  drawdown_abs?: number | string | null;
  ponr_score?: number | string | null;
  likely_mature?: number | boolean | null;
  ml_prob?: number | string | null;
  hard_exit?: number | boolean | null;
  hard_exit_reason?: string | null;
  delta?: number | string | null;
  gamma?: number | string | null;
  gamma_underlying_price?: number | string | null;
  theta?: number | string | null;
  vega?: number | string | null;
  implied_vol?: number | string | null;
  bid?: number | string | null;
  ask?: number | string | null;
  mid?: number | string | null;
  open_interest?: number | string | null;
  decision_posture?: string | null;
  decision_detail?: string | null;
  decision_priority?: number | string | null;
};

type RawHeldLeapRowPascal = {
  Conid?: number | string | null;
  Symbol?: string | null;
  Right?: string | null;
  Strike?: number | string | null;
  Expiration?: string | null;
  Quantity?: number | string | null;
  AvgPrice?: number | string | null;
  LastPrice?: number | string | null;
  MarketValue?: number | string | null;
  AccountId?: string | null;
  SubAccountId?: string | null;
  DTE?: number | string | null;
  AgeDays?: number | string | null;
  HighWaterMark?: number | string | null;
  DrawdownFromPeak?: number | string | null;
  DrawdownAbs?: number | string | null;
  DisplayName?: string | null;
  Multiplier?: number | string | null;
  Delta?: number | string | null;
  Gamma?: number | string | null;
  Theta?: number | string | null;
  Vega?: number | string | null;
  ImpliedVol?: number | string | null;
  LikelyPeaked?: boolean;
  ExitHintReason?: string | null;
  ExitHintScore?: number | string | null;
  ExitHintPercentile?: number | string | null;
  hardExit?: boolean;
  hardExitReason?: string | null;
  DecisionPosture?: string | null;
  DecisionDetail?: string | null;
  DecisionPriority?: number | string | null;
};


type RiskMetricsRow = {
  Conid?: number | string | null;
  conid?: number | string | null;
  Delta?: number | string | null;
  delta?: number | string | null;
  Gamma?: number | string | null;
  gamma?: number | string | null;
  Theta?: number | string | null;
  theta?: number | string | null;
  Vega?: number | string | null;
  vega?: number | string | null;
  ImpliedVol?: number | string | null;
  impliedVol?: number | string | null;
  implied_vol?: number | string | null;
  OI?: number | string | null;
  OpenInterest?: number | string | null;
  openInterest?: number | string | null;
  open_interest?: number | string | null;
  Bid?: number | string | null;
  bid?: number | string | null;
  Ask?: number | string | null;
  ask?: number | string | null;
  Mid?: number | string | null;
  mid?: number | string | null;
  LastPrice?: number | string | null;
  lastPrice?: number | string | null;
  mktPrice?: number | string | null;
  SnapshotAtUTC?: string | null;
  PriceSnapshotUTC?: string | null;
};

type RawFixedIncomeRow = {
  DisplayName?: string | null;
  symbol?: string | null;
  account_id?: string | null;
  market_value?: number | string | null;
  weight_pct?: number | string | null;
  weightpct?: number | string | null;
  target_pct?: number | string | null;
};

type RawFixedIncomeRowPascal = {
  Ticker?: string | null;
  Quantity?: number | string | null;
  MarketValue?: number | string | null;
};


/* -------------------------- Risk metrics hydration -------------------------- */
const normalizeConidKey = (value: unknown): string | null => {
  if (value === null || typeof value === "undefined") return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const asNum = Number(raw);
  return Number.isFinite(asNum) ? String(Math.trunc(asNum)) : raw;
};

const rowNeedsRiskHydration = (row: HeldLeapRow): boolean => {
  return (
    row.delta == null ||
    row.gamma == null ||
    row.theta == null ||
    row.vega == null ||
    row.impliedVol == null ||
    row.openInterest == null
  );
};

const metricConidKey = (row: RiskMetricsRow): string | null => {
  return normalizeConidKey(row.Conid ?? row.conid);
};

const toRiskMetricMap = (rows: RiskMetricsRow[]): Map<string, RiskMetricsRow> => {
  const out = new Map<string, RiskMetricsRow>();
  for (const row of rows) {
    const key = metricConidKey(row);
    if (!key) continue;
    const existing = out.get(key);
    if (!existing) {
      out.set(key, row);
      continue;
    }

    const existingScore = [existing.Delta ?? existing.delta, existing.Vega ?? existing.vega, existing.Theta ?? existing.theta, existing.ImpliedVol ?? existing.impliedVol ?? existing.implied_vol]
      .filter((x) => toNum(x) !== null).length;
    const nextScore = [row.Delta ?? row.delta, row.Vega ?? row.vega, row.Theta ?? row.theta, row.ImpliedVol ?? row.impliedVol ?? row.implied_vol]
      .filter((x) => toNum(x) !== null).length;
    if (nextScore > existingScore) out.set(key, row);
  }
  return out;
};

const mergeRiskMetricsIntoHeldLeaps = (rows: HeldLeapRow[], riskRows: RiskMetricsRow[]): HeldLeapRow[] => {
  if (!rows.length || !riskRows.length) return rows;
  const byConid = toRiskMetricMap(riskRows);
  if (!byConid.size) return rows;

  return rows.map((row) => {
    const key = normalizeConidKey(row.contractId);
    const risk = key ? byConid.get(key) : undefined;
    if (!risk) return row;

    const next: HeldLeapRow = {
      ...row,
      delta: row.delta ?? toNum(risk.Delta ?? risk.delta) ?? null,
      gamma: row.gamma ?? toNum(risk.Gamma ?? risk.gamma) ?? null,
      theta: row.theta ?? toNum(risk.Theta ?? risk.theta) ?? null,
      vega: row.vega ?? toNum(risk.Vega ?? risk.vega) ?? null,
      impliedVol: row.impliedVol ?? toPctDecimal(risk.ImpliedVol ?? risk.impliedVol ?? risk.implied_vol) ?? null,
      openInterest: row.openInterest ?? toInt(risk.OI ?? risk.OpenInterest ?? risk.openInterest ?? risk.open_interest) ?? null,
      bid: row.bid ?? toNum(risk.Bid ?? risk.bid) ?? null,
      ask: row.ask ?? toNum(risk.Ask ?? risk.ask) ?? null,
      mid: row.mid ?? toNum(risk.Mid ?? risk.mid) ?? null,
    };

    if (next.market == null) {
      next.market = toNum(risk.Mid ?? risk.mid ?? risk.LastPrice ?? risk.lastPrice ?? risk.mktPrice) ?? null;
    }
    return next;
  });
};

// Split out of the old hydrateHeldLeapsRiskFromMetrics so the REQUEST can be started
// in parallel with /get_portfolio_view rather than waiting on its response. The page
// used to make three serial round-trips before it painted anything
// (get_portfolio_view -> risk/leaps_metrics -> risk/nav_latest), which is most of why
// it is slow pre-market. The merge stays pure and is applied once both have landed.
// Never rejects except on abort, and callers guard that.
const fetchLeapsMetricsRows = async (signal?: AbortSignal): Promise<RiskMetricsRow[]> => {
  try {
    const res = await fetch(`${API_BASE_URL}/risk/leaps_metrics`, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!res.ok) return [];
    const payload = await res.json().catch(() => null);
    return Array.isArray(payload) ? payload : Array.isArray(payload?.rows) ? payload.rows : [];
  } catch (err: any) {
    if (err?.name === "AbortError") throw err;
    return [];
  }
};

/* -------------------------- Contract label helpers -------------------------- */
function fmtStrike(strike: number | null | undefined): string {
  if (typeof strike !== "number" || !isFinite(strike)) return "—";
  const isHalf =
    Math.abs(strike * 2 - Math.round(strike * 2)) < 1e-9 &&
    Math.abs(strike - Math.round(strike)) > 1e-9;
  if (isHalf) return strike.toFixed(1);
  return Math.round(strike).toString();
}

function fmtContractLabel(args: {
  symbol?: string | null;
  expiration?: string | null;
  right?: string | null;
  strike?: number | null;
  displayName?: string | null;
  contractId?: string | number | null;
  contractDesc?: string | null;
}): string {
  const symbol = (args.symbol || "").toString().trim();
  const exp = (args.expiration || "").toString().trim();
  const right = (args.right || "").toString().trim();
  const strike = typeof args.strike === "number" ? args.strike : null;

  if (symbol && exp && right && typeof strike === "number") {
    return `${symbol} ${exp} ${right.toUpperCase()} ${fmtStrike(strike)}`;
  }

  const dn = (args.displayName || "").toString().trim();
  if (dn) return dn;

  const cd = (args.contractDesc || "").toString().trim();
  if (cd) return cd;

  if (args.contractId !== null && typeof args.contractId !== "undefined") {
    return String(args.contractId);
  }

  return "—";
}

/* -------------------------- Meta merge / fallback util ------------------------- */
/* ----------------------------- Rebalance helpers ------------------------------ */
function normalizeRebalanceMeta(x: any): RebalanceMeta {
  if (!x || typeof x !== "object") return null;
  const obj = x.leaps && typeof x.leaps === "object" ? x.leaps : x;

  return {
    regime: (obj.regime ?? obj.name ?? null) as any,
    effective_regime: (obj.effective_regime ?? obj.effectiveRegime ?? obj.regime ?? obj.name ?? null) as any,
    phase: obj.phase ?? null,
    vix_zscore: toNum(obj.vix_zscore ?? obj.vixZScore) ?? null,
    vix_zscore_yesterday: toNum(obj.vix_zscore_yesterday ?? obj.vixZScoreYesterday) ?? null,
    delta_z: toNum(obj.delta_z ?? obj.deltaZ) ?? null,
    vix_current: toNum(obj.vix_current ?? obj.vixCurrent) ?? null,
    vix_delta: toNum(obj.vix_delta ?? obj.vixDelta) ?? null,
    live_vix_used: Boolean(obj.live_vix_used ?? obj.vixLiveUsed ?? false),
    target_pct_raw: toPctDecimal(obj.target_pct_raw ?? obj.targetPctRaw) ?? null,
    target_pct_capped: toPctDecimal(obj.target_pct_capped ?? obj.targetPctCapped) ?? null,
    target_pct_effective: toPctDecimal(obj.target_pct_effective ?? obj.targetPctEffective) ?? null,
    target_source: (obj.target_source ?? obj.targetSource ?? null) as any,
    rebalance_tolerance_pct: toNum(obj.rebalance_tolerance_pct ?? obj.rebalanceTolerancePct) ?? null,
    vix_trim_enabled:
      typeof obj.vix_trim_enabled === "boolean"
        ? obj.vix_trim_enabled
        : typeof obj.vixTrimEnabled === "boolean"
        ? obj.vixTrimEnabled
        : undefined,
    leaps_nav: toNum(obj.leaps_nav ?? obj.leapsNAV) ?? null,
    portfolio_nav: toNum(obj.portfolio_nav ?? obj.portfolioNAV) ?? null,
    current_weight: toPctDecimal(obj.current_weight ?? obj.currentWeight) ?? null,
    desired_notional: toNum(obj.desired_notional ?? obj.desiredNotional) ?? null,
    trim_notional: toNum(obj.trim_notional ?? obj.trimNotional) ?? null,
    hard_cap_trim: toNum(obj.hard_cap_trim ?? obj.hardCapTrim) ?? null,
    suggested_trim_to_target: toNum(obj.suggested_trim_to_target ?? obj.suggestedTrimToTarget) ?? null,
    rebalance_source: (obj.rebalance_source ?? obj.rebalanceSource ?? null) as any,
    rebalance_engine_available: (obj.rebalance_engine_available ?? obj.rebalanceEngineAvailable ?? null) as any,
    rebalance_note: (obj.rebalance_note ?? obj.rebalanceNote ?? null) as any,
    market_weight_over_funded_cap: (obj.market_weight_over_funded_cap ?? obj.marketWeightOverFundedCap ?? null) as any,
    trim_reason: (obj.trim_reason ?? obj.trimReason ?? null) as any,
    pending_harvest: typeof obj.pending_harvest === "boolean" ? obj.pending_harvest : undefined,
    harvest_window: typeof obj.harvest_window === "boolean" ? obj.harvest_window : undefined,
    reversion_confirm_days: toInt(obj.reversion_confirm_days) ?? undefined,
    defense_by_z: typeof obj.defense_by_z === "boolean" ? obj.defense_by_z : undefined,
    defense_confirmed: typeof obj.defense_confirmed === "boolean" ? obj.defense_confirmed : undefined,
    block_reason: (obj.block_reason ?? null) as any,
    cooldown_active: typeof obj.cooldown_active === "boolean" ? obj.cooldown_active : undefined,
    caps:
      obj.caps && typeof obj.caps === "object"
        ? {
            funded_max: toPctDecimal(obj.caps.funded_max) ?? null,
            growth_max: toPctDecimal(obj.caps.growth_max) ?? null,
          }
        : null,
    early_defense:
      obj.early_defense && typeof obj.early_defense === "object"
        ? {
            enabled:
              typeof obj.early_defense.enabled === "boolean"
                ? obj.early_defense.enabled
                : undefined,
            active:
              typeof obj.early_defense.active === "boolean"
                ? obj.early_defense.active
                : undefined,
            target_pct:
              toPctDecimal(obj.early_defense.target_pct ?? obj.early_defense.targetPct) ?? null,
            reason: (obj.early_defense.reason ?? null) as any,
            conditions:
              obj.early_defense.conditions && typeof obj.early_defense.conditions === "object"
                ? obj.early_defense.conditions
                : {},
          }
        : null,
    spx: obj.spx && typeof obj.spx === "object" ? obj.spx : null,
    term: obj.term && typeof obj.term === "object" ? obj.term : null,
  };
}

function normalizeRebalanceOrdersPreview(arr: any): RebalanceOrderPreview[] {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((o: any) => {
      if (!o || typeof o !== "object") return null;
      return {
        type: (o.type ?? o.Action ?? o.action ?? "SELL") as any,
        contract_id: (o.contract_id ?? o.contractId ?? o.ContractId ?? null) as any,
        estimated_qty: (o.estimated_qty ?? o.estimatedQty ?? o.EstimatedQty ?? null) as any,
        target_notional: (o.target_notional ?? o.targetNotional ?? o.TargetNotional ?? null) as any,
        reason: (o.reason ?? o.Reason ?? null) as any,
        display_name: (o.display_name ?? o.display ?? o.DisplayName ?? null) as any,
        symbol: (o.symbol ?? o.Symbol ?? null) as any,
        right: (o.right ?? o.Right ?? null) as any,
        strike: toNum(o.strike ?? o.Strike) ?? null,
        expiration: (o.expiration ?? o.Expiration ?? null) as any,
        contractDesc: (o.contractDesc ?? o.ContractDesc ?? null) as any,
      };
    })
    .filter(Boolean) as RebalanceOrderPreview[];
}

function normalizePendingOrders(arr: any): PendingOrderRow[] {
  if (!Array.isArray(arr)) return [];

  return arr.map((o: any) => ({
    orderId: toInt(o.orderId ?? o.OrderId) ?? undefined,
    action: (o.action ?? o.Action ?? null) as any,
    contractId: (o.contractId ?? o.ContractId ?? null) as any,
    displayName: (o.displayName ?? o.DisplayName ?? null) as any,
    symbol: (o.symbol ?? o.Symbol ?? o.underlyingSymbol ?? o.UnderlyingSymbol ?? null) as any,
    right: (o.right ?? o.Right ?? o.optRight ?? o.OptRight ?? null) as any,
    strike: toNum(o.strike ?? o.Strike ?? o.optStrike ?? o.OptStrike) ?? null,
    expiration: (o.expiration ?? o.Expiration ?? o.optExpiration ?? o.OptExpiration ?? null) as any,
    contractDesc:
      (o.contractDesc ?? o.ContractDesc ?? o.optContractDesc ?? o.OptContractDesc ?? null) as any,
    scheduledDate: (o.scheduledDate ?? o.ScheduledDate ?? null) as any,
    estimatedQty: toInt(o.estimatedQty ?? o.EstimatedQty) ?? null,
    targetNotional: toNum(o.targetNotional ?? o.TargetNotional) ?? null,
    overrideQty: toInt(o.overrideQty ?? o.OverrideQty) ?? null,
    overrideNotional: toNum(o.overrideNotional ?? o.OverrideNotional) ?? null,
    targetAllocation: toPctDecimal(o.targetAllocation ?? o.TargetAllocation) ?? null,
    currentAllocation: toPctDecimal(o.currentAllocation ?? o.CurrentAllocation) ?? null,
    reason: (o.reason ?? o.Reason ?? null) as any,
    status: (o.status ?? o.Status ?? null) as any,
    createdAt: (o.createdAt ?? o.CreatedAt ?? null) as any,
    updatedAt: (o.updatedAt ?? o.UpdatedAt ?? null) as any,
    approvedAt: (o.approvedAt ?? o.ApprovedAt ?? null) as any,
    mlScore: toNum(o.mlScore ?? o.MLScore) ?? null,
    mlPercentile: toNum(o.mlPercentile ?? o.MLPercentile) ?? null,
    tags: (o.tags ?? o.Tags ?? null) as any,
  }));
}

/* get_portfolio_view returns SELECT TOP 50 staged orders
 * (strategy/portfolio_view.py `_read_pending_orders(limit=50)`). When exactly this many
 * come back the real total is unknown, not 50. Keep this in step with the backend. */
const PENDING_ORDERS_PAYLOAD_CAP = 50;

function summarizePendingOrders(pending: PendingOrderRow[]) {
  const out = {
    total: pending.length,
    /** True when the payload came back at the cap, so `total` is a FLOOR, not a count. */
    capped: pending.length >= PENDING_ORDERS_PAYLOAD_CAP,
    sells: 0,
    buys: 0,
    fi: 0,
    topReasons: [] as Array<[string, number]>,
    lastUpdatedUTC: null as string | null,
  };

  const reasons: Record<string, number> = {};
  const times: number[] = [];

  for (const o of pending) {
    const a = (o.action || "").toString().toUpperCase();
    if (a === "SELL" || a === "ROLL" || a === "TRIM") out.sells += 1;
    else if (a === "BUY") out.buys += 1;

    const r = (o.reason || "").toString().trim();
    if (r) reasons[r] = (reasons[r] ?? 0) + 1;

    const ts = o.updatedAt || o.createdAt || null;
    if (ts) {
      const n = Date.parse(ts);
      if (Number.isFinite(n)) times.push(n);
    }

    const tags = (o.tags || "").toString().toUpperCase();
    if (tags.includes("FI") || (o.contractId || "").toString().toUpperCase().startsWith("FI:")) {
      out.fi += 1;
    }
  }

  out.topReasons = Object.entries(reasons)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  if (times.length) {
    const d = new Date(Math.max(...times));
    out.lastUpdatedUTC = d.toISOString().replace("T", " ").replace("Z", " UTC");
  }

  return out;
}

/* ----------------------------- Compact Sleeve Stat ----------------------------- */
const SleeveStat: React.FC<{ label: string; target: number; current: number }> = ({
  label,
  target,
  current,
}) => {
  const delta = target - current;
  const pos = delta >= 0;

  return (
    <div className="flex items-center gap-4 rounded-lg px-3 py-2 text-sm" style={sectionCardStyle}>
      <div className="font-medium" style={{ color: ui.textPrimary }}>
        {label}
      </div>
      <div className="flex items-center gap-3">
        <div>
          <span style={smallMutedText}>Target</span>{" "}
          <strong style={{ color: ui.textPrimary }}>{pct(target, 1)}</strong>
        </div>
        <div>
          <span style={smallMutedText}>Current</span>{" "}
          <strong style={{ color: ui.textPrimary }}>{pct(current, 1)}</strong>
        </div>
        <div>
          <span style={smallMutedText}>Δ</span>{" "}
          <strong style={pos ? metricPositiveText : metricNegativeText}>
            {pos ? "+" : ""}
            {pct(delta, 1)}
          </strong>
        </div>
      </div>
    </div>
  );
};

/* --------------------------- LEAPS DTE bucket table --------------------------- */
type DteBucket = {
  key: string;
  label: string;
  shortLabel: string;
  description: string;
  min: number | null;
  max: number | null;
  tone: Tone;
};

const DTE_BUCKETS: DteBucket[] = [
  { key: "lt365", shortLabel: "<365", label: "Mature / <365 DTE", description: "Transitioning out of LEAPS; review roll/exit discipline.", min: null, max: 364, tone: "danger" },
  { key: "365_599", shortLabel: "365–599", label: "Aging / 365–599 DTE", description: "Below new-entry policy; watch theta and roll timing.", min: 365, max: 599, tone: "warning" },
  { key: "600_899", shortLabel: "600–899", label: "Core / 600–899 DTE", description: "Policy-eligible LEAPS zone.", min: 600, max: 899, tone: "success" },
  { key: "900_1199", shortLabel: "900–1199", label: "Long / 900–1199 DTE", description: "Longer runway and lower theta load.", min: 900, max: 1199, tone: "info" },
  { key: "ge1200", shortLabel: "1200+", label: "Ultra-long / 1200+ DTE", description: "Maximum runway; monitor liquidity and concentration.", min: 1200, max: null, tone: "neutral" },
  { key: "unknown", shortLabel: "Unknown", label: "Unknown DTE", description: "Missing reliable expiration/DTE telemetry.", min: null, max: null, tone: "neutral" },
];

type DteGroup = {
  bucket: DteBucket;
  rows: HeldLeapRow[];
  marketValue: number;
  costBasis: number;
  avgDte: number | null;
  ret: number | null;
};

function rowDte(row: HeldLeapRow): number | null {
  const direct = toInt((row as any).dte);
  if (direct !== null) return direct;
  const exp = (row as any).exp;
  if (!exp) return null;
  const d = new Date(exp);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  return Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function dteBucketFor(row: HeldLeapRow): DteBucket {
  const dte = rowDte(row);
  if (dte === null) return DTE_BUCKETS[DTE_BUCKETS.length - 1];
  return (
    DTE_BUCKETS.find((b) => b.key !== "unknown" && (b.min === null || dte >= b.min) && (b.max === null || dte <= b.max)) ??
    DTE_BUCKETS[DTE_BUCKETS.length - 1]
  );
}

function leapMarketValue(row: any): number {
  const value = toNum(row.value ?? row.market_value ?? row.MarketValue);
  if (value !== null) return value;
  const price = toNum(row.last ?? row.marketPrice ?? row.market_price ?? row.LastPrice);
  const qty = toNum(row.qty ?? row.quantity ?? row.Quantity);
  return price !== null && qty !== null ? price * qty * 100 : 0;
}

function leapCostBasis(row: any): number {
  const entry = toNum(row.entry ?? row.entryPrice ?? row.entry_price ?? row.AvgPrice);
  const qty = toNum(row.qty ?? row.quantity ?? row.Quantity);
  return entry !== null && qty !== null ? entry * qty * 100 : 0;
}

function buildDteGroup(bucket: DteBucket, rows: HeldLeapRow[]): DteGroup {
  const marketValue = rows.reduce((sum, row) => sum + leapMarketValue(row), 0);
  const costBasis = rows.reduce((sum, row) => sum + leapCostBasis(row), 0);
  const dtes = rows.map(rowDte).filter((x): x is number => typeof x === "number" && isFinite(x));
  const avgDte = dtes.length ? dtes.reduce((a, b) => a + b, 0) / dtes.length : null;
  const ret = costBasis > 0 ? marketValue / costBasis - 1 : null;
  return { bucket, rows, marketValue, costBasis, avgDte, ret };
}

function rowDisplayName(row: HeldLeapRow): string {
  const display = (row.display || "").toString().trim();
  if (display) return display;

  return fmtContractLabel({
    symbol: "SPX",
    expiration: row.exp ?? null,
    right: row.right ?? null,
    strike: typeof row.strike === "number" ? row.strike : null,
    contractId: row.contractId ?? null,
  });
}

// The ABSOLUTE exit signal for this line, 0-100. Prefers exit_hint_score (rule+model
// blend); falls back to the within-book rank ONLY when no score exists, which is the
// degenerate case. Used for sorting and for the >=80/>=90 attention counts, both of
// which need an absolute measure -- applying them to a rank would recreate the
// backend defect fixed in 0652a177, where a fixed number of lines always cross the
// threshold regardless of how calm the book is.
function rowExitScorePercent(row: HeldLeapRow): number | null {
  const score = toNum((row as any).exitHintScore);
  if (score !== null) return score > 1.5 ? clamp(score, 0, 100) : clamp(score * 100, 0, 100);

  const percentile = toNum((row as any).exitHintPercentile);
  return percentile === null ? null : clamp(percentile, 0, 100);
}

// The WITHIN-BOOK RANK and its denominator. This is a position, not a level: with N
// holdings the values are forced to 100/N, 200/N ... 100 every day, so "96" means
// "second-worst today", NOT "96% risk". Rendered with the denominator so that is
// unmistakable. Backend supplies exit_risk_rank_of (2026-07-31).
function rowExitRank(row: HeldLeapRow): { pct: number; of: number | null } | null {
  const pct = toNum((row as any).exitHintPercentile);
  if (pct === null) return null;
  const of = toNum((row as any).exitRiskRankOf);
  return { pct: clamp(pct, 0, 100), of: of === null ? null : Math.round(of) };
}

function rowPostureTone(row: HeldLeapRow): Tone {
  if ((row as any).hardExit === true) return "danger";
  if ((row as any).likelyPeaked === true) return "warning";
  const priority = toNum((row as any).decisionPriority);
  if (priority !== null && priority >= 80) return "danger";
  if (priority !== null && priority >= 50) return "warning";
  const risk = rowExitScorePercent(row);
  if (risk !== null && risk >= 80) return "warning";
  return "neutral";
}

// Exit-risk is surfaced as a within-book RANK + tier, never a 0–100% "sell probability".
// Validation (HelionBacktest/exit_ml_validation) showed the raw score is a saturated,
// regime-dependent, miscalibrated number that mostly restates drawdown/DTE and barely beats
// a simple drawdown+DTE rule outside of stress — so it is honest to present it as a ranking
// and anchor decisions on the posture + legible drivers, not on the number.
const EXIT_RANK_TOOLTIP =
  "Exit-risk rank: this line's PLACE in today's book on the combined rule + model exit signals — " +
  "'2 of 14' means second-most exit-worthy of 14 holdings today. It is an ORDER, not a level: " +
  "somebody is always 1st, even when the whole book is calm and every underlying score is near zero. " +
  "It carries no information about whether anything is actually wrong — read the Exit score beside it " +
  "for that, and the posture and drivers for what to do. As of 2026-07-31 this rank no longer drives " +
  "any posture escalation, because a fixed number of lines topped it every day regardless of risk.";

// The within-book rank, rendered as an ORDINAL with its denominator: "2nd of 14".
// Previously this called rowExitRiskPercent, which returns the SCORE whenever one
// exists -- so the cell labelled "Exit rank" was showing the score, identical to the
// "Exit score" cell beside it, and the real rank was never displayed at all.
function fmtExitRank(row: HeldLeapRow): string {
  const r = rowExitRank(row);
  if (r === null) return "—";
  if (r.of && r.of > 0) {
    // rank(pct)*100 -> ordinal position from the WORST end. pct=100 is worst.
    const place = Math.max(1, Math.round(((100 - r.pct) / 100) * r.of) + 1);
    return `${place} of ${r.of}`;
  }
  return `P${r.pct.toFixed(0)}`;
}

// The ABSOLUTE exit signal (0–100), distinct from the within-book rank. Shown beside the
// rank so a high rank on a calm book (e.g. rank 100 but a 26 absolute score) is obvious.
const EXIT_SCORE_TOOLTIP =
  "Exit score: the ABSOLUTE exit signal for this line (rule + model blend, 0–100), separate " +
  "from the within-book rank. The rank can read high while this is low when the whole book is " +
  "calm — a high rank with a low score means 'most exit-worthy of a quiet book', not urgent.";

function rowExitRawScorePct(row: HeldLeapRow): number | null {
  const raw = toNum((row as any).exitHintRawScore);
  if (raw === null) return null;
  return raw > 1.5 ? clamp(raw, 0, 100) : clamp(raw * 100, 0, 100);
}

type PositionSortMode = "risk" | "value" | "dte" | "return";

function fmtNumber(v: number | null | undefined, dp = 2): string {
  return typeof v === "number" && isFinite(v) ? v.toFixed(dp) : "—";
}

function fmtInteger(v: number | null | undefined): string {
  return typeof v === "number" && isFinite(v) ? Math.round(v).toLocaleString() : "—";
}

function rowCostBasis(row: HeldLeapRow): number {
  const explicit = toNum((row as any).costBasis);
  if (explicit !== null) return explicit;
  return leapCostBasis(row);
}

function rowUnrealizedPnl(row: HeldLeapRow): number | null {
  const explicit = toNum((row as any).unrealizedPnl);
  if (explicit !== null) return explicit;
  const value = leapMarketValue(row);
  const cost = rowCostBasis(row);
  if (!isFinite(value) || !isFinite(cost) || cost <= 0) return null;
  return value - cost;
}

function rowPrice(row: HeldLeapRow): number | null {
  return toNum(row.market ?? row.mid ?? row.bid ?? row.ask ?? null);
}

function sortPositionRows(rows: HeldLeapRow[], mode: PositionSortMode): HeldLeapRow[] {
  const sorted = [...(rows ?? [])];
  if (mode === "risk") {
    sorted.sort((a, b) => {
      const ah = (a as any).hardExit ? 1 : 0;
      const bh = (b as any).hardExit ? 1 : 0;
      if (ah !== bh) return bh - ah;
      const ap = toNum((a as any).decisionPriority) ?? 0;
      const bp = toNum((b as any).decisionPriority) ?? 0;
      if (ap !== bp) return bp - ap;
      const ar = rowExitScorePercent(a) ?? -1;
      const br = rowExitScorePercent(b) ?? -1;
      if (ar !== br) return br - ar;
      return leapMarketValue(b) - leapMarketValue(a);
    });
    return sorted;
  }
  if (mode === "dte") {
    sorted.sort((a, b) => {
      const ad = rowDte(a) ?? 99999;
      const bd = rowDte(b) ?? 99999;
      if (ad !== bd) return ad - bd;
      return leapMarketValue(b) - leapMarketValue(a);
    });
    return sorted;
  }
  if (mode === "return") {
    sorted.sort((a, b) => (toNum(b.returnPct) ?? -999) - (toNum(a.returnPct) ?? -999));
    return sorted;
  }
  sorted.sort((a, b) => leapMarketValue(b) - leapMarketValue(a));
  return sorted;
}

const CompactFact: React.FC<{
  label: string;
  value: React.ReactNode;
  tone?: "positive" | "negative" | "neutral";
  title?: string;
}> = ({ label, value, tone = "neutral", title }) => {
  const valueStyle: React.CSSProperties =
    tone === "positive" ? metricPositiveText : tone === "negative" ? metricNegativeText : { color: ui.textPrimary };

  return (
    <div className="rounded border px-2 py-1.5" style={inlineBoxStyle} title={title}>
      <div className="text-[10px] uppercase tracking-wide" style={smallMutedText}>{label}</div>
      <div className="mt-0.5 truncate text-sm font-semibold" style={valueStyle}>{value}</div>
    </div>
  );
};

const CompactPositionStrip: React.FC<{
  row: HeldLeapRowX;
  rank?: number;
  contextLabel?: string;
  showReason?: boolean;
}> = ({ row, rank, contextLabel, showReason = true }) => {
  const exitScorePct = rowExitRawScorePct(row);
  const tone = rowPostureTone(row);
  const dte = rowDte(row);
  const marketValue = leapMarketValue(row);
  const costBasis = rowCostBasis(row);
  const pnl = rowUnrealizedPnl(row);
  const pnlTone: "positive" | "negative" | "neutral" =
    typeof pnl === "number" && pnl >= 0 ? "positive" : typeof pnl === "number" ? "negative" : "neutral";
  const returnTone: "positive" | "negative" | "neutral" =
    typeof row.returnPct === "number" && row.returnPct >= 0 ? "positive" : typeof row.returnPct === "number" ? "negative" : "neutral";
  const reason = (row.hardExitReason || row.decisionDetail || row.exitHintReason || "No signal detail returned").toString();
  const updatedAt = (row as HeldLeapRow & { updatedAt?: string | null }).updatedAt ?? null;
  const priceMove = `${dollars(row.entry)} → ${dollars(rowPrice(row))}`;
  const quoteBits = [
    row.bid !== null && typeof row.bid === "number" ? `B ${dollars(row.bid)}` : null,
    row.mid !== null && typeof row.mid === "number" ? `M ${dollars(row.mid)}` : null,
    row.ask !== null && typeof row.ask === "number" ? `A ${dollars(row.ask)}` : null,
  ].filter(Boolean).join(" / ");

  const facts: Array<{ label: string; value: React.ReactNode; tone?: "positive" | "negative" | "neutral"; title?: string }> = [
    { label: "DTE", value: dte ?? "—" },
    { label: "Age", value: fmtInteger(row.ageDays ?? null) },
    { label: "MV", value: dollars(marketValue, 0) },
    { label: "P/L", value: pnl === null ? "—" : dollars(pnl, 0), tone: pnlTone },
    { label: "Ret", value: pct(row.returnPct, 1), tone: returnTone },
    { label: "DD peak", value: row.ddFromPeak == null ? "—" : pct(-Math.abs(row.ddFromPeak), 1), tone: row.ddFromPeak ? "negative" : "neutral" },
    { label: "Exit rank", value: fmtExitRank(row), title: EXIT_RANK_TOOLTIP },
    { label: "Exit score", value: exitScorePct === null ? "—" : exitScorePct.toFixed(0), title: EXIT_SCORE_TOOLTIP },
    { label: "Priority", value: fmtNumber(row.decisionPriority ?? null, 0) },
    { label: "Entry → market", value: priceMove, title: priceMove },
    { label: "Greeks", value: `Δ ${fmtNumber(row.delta ?? null, 2)} / V ${fmtNumber(row.vega ?? null, 1)}`, title: `Delta ${fmtNumber(row.delta ?? null, 2)} / Vega ${fmtNumber(row.vega ?? null, 2)} / Theta ${fmtNumber(row.theta ?? null, 2)} / Gamma ${fmtNumber(gammaPer1Pct(row), 3)} delta per 1% index move` },
    { label: "IV / OI", value: `${pct(row.impliedVol, 1)} / ${fmtInteger(row.openInterest ?? null)}` },
    { label: "Quote", value: quoteBits || "—", title: quoteBits || undefined },
  ];

  return (
    <div className="rounded border px-3 py-2" style={inlineBoxStyle}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-[260px] flex-[1.4]">
          <div className="flex flex-wrap items-center gap-2">
            {typeof rank === "number" ? <span className="text-[11px] font-semibold" style={smallMutedText}>#{rank}</span> : null}
            <span className="rounded px-2 py-0.5 text-xs" style={badgeStyle(tone)}>
              {row.decisionPosture || (row.hardExit ? "Act now" : row.likelyPeaked ? "Review" : "Monitor")}
            </span>
            {contextLabel ? <span className="rounded border px-2 py-0.5 text-xs" style={inlineBoxStyle}>{contextLabel}</span> : null}
            {row.hardExit ? (
              <span className="rounded px-2 py-0.5 text-xs" style={badgeStyle("danger")}>Hard exit</span>
            ) : row.hardExitEvaluated === false ? (
              // Not "clear" -- UNEVALUATED. Nothing writes dbo.HeldLeaps.hard_sell, so the
              // absence of the badge above is not evidence of anything.
              <span
                className="rounded px-2 py-0.5 text-xs opacity-70"
                style={badgeStyle("neutral")}
                title="Hard-exit is NOT evaluated by any producer - dbo.HeldLeaps.hard_sell has no writer. The absence of a Hard exit badge means this check does not run, NOT that the position was checked and found clear."
              >
                Hard exit: not evaluated
              </span>
            ) : null}
            {row.likelyPeaked ? <span className="rounded px-2 py-0.5 text-xs" style={badgeStyle("warning")}>Likely peaked</span> : null}
            {row.likelyMature ? <span className="rounded px-2 py-0.5 text-xs" style={badgeStyle("warning")}>Mature</span> : null}
          </div>
          <div className="mt-1.5 font-semibold" style={{ color: ui.textPrimary }} title={rowDisplayName(row)}>
            {rowDisplayName(row)}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px]" style={smallMutedText}>
            <span>Acct <strong style={{ color: ui.textPrimary }}>{row.account || "—"}</strong></span>
            <span>Exp. <strong style={{ color: ui.textPrimary }}>{toYMD(row.exp)}</strong></span>
            <span>{row.right ?? "—"} <strong style={{ color: ui.textPrimary }}>{typeof row.strike === "number" ? fmtStrike(row.strike) : "—"}</strong></span>
            <span>Qty <strong style={{ color: ui.textPrimary }}>{fmtInteger(row.qty)}</strong></span>
            {costBasis > 0 ? <span>Cost <strong style={{ color: ui.textPrimary }}>{dollars(costBasis, 0)}</strong></span> : null}
            {/* "Price updated", not "Updated". This is HeldLeaps.UpdatedAt, which
                refresh_portfolio_timer bumps at 06:15 MT — it attests the PRICE fields and
                nothing else. It does NOT cover peak_return / drawdown_from_peak, which a
                different job writes at 13:30 MT. When that job crashed for ~13 days in
                August 2026, this line read "Updated 06:15 this morning" beside a frozen
                "% off peak" trigger. One stamp over two data sources, naming only the
                healthier one. Peak freshness is reported separately (peakDataFreshness). */}
            {updatedAt ? <span>Price updated <strong style={{ color: ui.textPrimary }}>{fmtLocalDateTime(updatedAt)}</strong></span> : null}
          </div>
          {/* CIO-approved 2026-08-31 (Stage 1). A one-contract line CANNOT produce a harvest
              ticket: the 50% trim rounds to zero and the engine only tickets above zero. Ten
              of 28 lines are in this state and they are the book's best performers. This is
              deliberately on the POSITION row, not the exit watchlist — the watchlist filters
              to positions under exit pressure, so a line at 1.26x would never appear there,
              and these need saying BEFORE they reach 2x, not at the moment they cross. */}
          {row.singleContractConstraint ? (
            <div className="mt-1.5 text-[11px]" style={secondaryText}>
              <span
                className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
                style={badgeStyle("warning")}
              >
                No partial trim
              </span>{" "}
              A partial trim is not available: the policy needs at least{" "}
              {row.minContractsForTrim ?? 2} contracts to split a position, and this line holds{" "}
              {fmtInteger(row.qty)}. It is a hold-or-close decision.
            </div>
          ) : null}
          {showReason ? <div className="mt-1.5 text-xs" style={secondaryText}>{reason}</div> : null}
        </div>

        <div className="grid flex-1 gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(82px, 1fr))" }}>
          {facts.map((fact) => (
            <CompactFact key={fact.label} label={fact.label} value={fact.value} tone={fact.tone} title={fact.title} />
          ))}
        </div>
      </div>
    </div>
  );
};

const CompactPositionList: React.FC<{
  rows: HeldLeapRow[];
  maxRows?: number;
  sortMode?: PositionSortMode;
  emptyText?: string;
  contextLabel?: string;
}> = ({ rows, maxRows = 8, sortMode, emptyText = "No lines to display.", contextLabel }) => {
  const resolvedSortMode: PositionSortMode = sortMode ?? "risk";
  const rankedRows = React.useMemo(() => sortPositionRows(rows ?? [], resolvedSortMode).slice(0, maxRows), [rows, resolvedSortMode, maxRows]);

  if (!rankedRows.length) {
    return <div className="text-xs" style={smallMutedText}>{emptyText}</div>;
  }

  return (
    <div className="grid gap-2">
      {rankedRows.map((row, idx) => (
        <CompactPositionStrip
          key={`${row.contractId}-${row.exp}-${row.strike}-${row.qty}-${idx}`}
          row={row}
          rank={idx + 1}
          contextLabel={contextLabel}
        />
      ))}
      {(rows ?? []).length > rankedRows.length ? (
        <div className="text-xs" style={smallMutedText}>
          Showing {rankedRows.length} of {(rows ?? []).length} lines. Open Detailed LEAPS Ledger for the complete audit table.
        </div>
      ) : null}
    </div>
  );
};


const LeapsRiskSnapshotPanel: React.FC<{ rows: HeldLeapRow[] }> = ({ rows }) => {
  const stats = React.useMemo(() => {
    const source = rows ?? [];
    const totalMv = source.reduce((sum, row) => sum + leapMarketValue(row), 0);
    const totalCost = source.reduce((sum, row) => sum + rowCostBasis(row), 0);
    const dtes = source.map(rowDte).filter((x): x is number => typeof x === "number" && isFinite(x));
    const avgDte = dtes.length ? dtes.reduce((a, b) => a + b, 0) / dtes.length : null;
    const weightedDte = totalMv > 0 ? source.reduce((sum, row) => sum + (rowDte(row) ?? 0) * leapMarketValue(row), 0) / totalMv : null;
    const weightedDelta = totalMv > 0 ? source.reduce((sum, row) => sum + (toNum(row.delta) ?? 0) * leapMarketValue(row), 0) / totalMv : null;
    const weightedIv = totalMv > 0 ? source.reduce((sum, row) => sum + (toNum(row.impliedVol) ?? 0) * leapMarketValue(row), 0) / totalMv : null;
    const totalQty = source.reduce((sum, row) => sum + (toNum(row.qty) ?? 0), 0);
    const reviewLines = source.filter((row) => row.hardExit || row.likelyPeaked || (rowExitScorePercent(row) ?? 0) >= 80 || (toNum(row.decisionPriority) ?? 0) >= 50).length;
    const agingLines = source.filter((row) => {
      const dte = rowDte(row);
      return dte !== null && dte < 600;
    }).length;
    const quoteCoverage = source.length ? source.filter((row) => row.bid !== null || row.ask !== null || row.mid !== null).length / source.length : null;
    const oiMissing = source.filter((row) => row.openInterest === null || typeof row.openInterest === "undefined").length;
    const missingGreeks = source.filter((row) => row.delta === null && row.vega === null && row.theta === null).length;
    const topLine = source.reduce((best: HeldLeapRow | null, row) => (!best || leapMarketValue(row) > leapMarketValue(best) ? row : best), null);
    const topLinePct = topLine && totalMv > 0 ? leapMarketValue(topLine) / totalMv : null;
    return { totalMv, totalCost, totalQty, avgDte, weightedDte, weightedDelta, weightedIv, reviewLines, agingLines, quoteCoverage, oiMissing, missingGreeks, topLine, topLinePct };
  }, [rows]);

  const bookReturn = stats.totalCost > 0 ? stats.totalMv / stats.totalCost - 1 : null;
  const pnl = stats.totalMv - stats.totalCost;
  const qualityTone: Tone = stats.missingGreeks > 0 || stats.oiMissing > 0 ? "warning" : "success";

  return (
    <Card style={themeCardStyle}>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>LEAPS Book Intelligence</div>
            <div className="text-xs" style={secondaryText}>
              Executive book context, concentration, quote quality, and risk posture without another holdings table.
              {" "}Book-level greek exposure lives on{" "}
              <Link to="/risk" style={{ color: ui.accentInfo }} className="underline-offset-2 hover:underline">
                Risk
              </Link>
              , which reads them from the server and says whether they are live or close-confirmed.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded border px-2 py-0.5" style={inlineBoxStyle}>{rows.length} line{rows.length === 1 ? "" : "s"}</span>
            <span className="rounded px-2 py-0.5" style={badgeStyle(stats.reviewLines > 0 ? "warning" : "success")}>Review {stats.reviewLines}</span>
            <span className="rounded px-2 py-0.5" style={badgeStyle(stats.agingLines > 0 ? "warning" : "success")}>DTE &lt; 600: {stats.agingLines}</span>
            <span className="rounded px-2 py-0.5" style={badgeStyle(qualityTone)}>Data gaps {stats.missingGreeks + stats.oiMissing}</span>
          </div>
        </div>

        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))" }}>
          <CompactFact label="Option MV" value={dollars(stats.totalMv, 0)} />
          <CompactFact label="Cost basis" value={stats.totalCost > 0 ? dollars(stats.totalCost, 0) : "—"} />
          <CompactFact label="Unreal. P/L" value={stats.totalCost > 0 ? dollars(pnl, 0) : "—"} tone={pnl >= 0 ? "positive" : "negative"} />
          <CompactFact label="Book return" value={pct(bookReturn, 1)} tone={bookReturn !== null && bookReturn >= 0 ? "positive" : bookReturn !== null ? "negative" : "neutral"} />
          <CompactFact label="Contracts" value={fmtInteger(stats.totalQty)} />
          <CompactFact label="Top line %" value={stats.topLinePct === null ? "—" : pct(stats.topLinePct, 1)} />
          <CompactFact label="Avg / wtd DTE" value={`${stats.avgDte === null ? "—" : stats.avgDte.toFixed(0)} / ${stats.weightedDte === null ? "—" : stats.weightedDte.toFixed(0)}`} />
          <CompactFact label="Wtd delta" value={fmtNumber(stats.weightedDelta, 2)} />
          <CompactFact label="Wtd IV" value={pct(stats.weightedIv, 1)} />
          <CompactFact label="Quote coverage" value={stats.quoteCoverage === null ? "—" : pct(stats.quoteCoverage, 0)} />
          <CompactFact label="Missing OI" value={fmtInteger(stats.oiMissing)} tone={stats.oiMissing > 0 ? "negative" : "neutral"} />
        </div>

        {stats.topLine ? (
          <div className="rounded border p-3" style={sectionCardStyle}>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>Largest Concentration Line</div>
                <div className="text-xs" style={smallMutedText}>
                  Largest market-value line. Full sort, filters, Greeks, and audit trail remain in Detailed LEAPS Ledger.
                </div>
              </div>
              <span className="rounded border px-2 py-0.5 text-xs" style={inlineBoxStyle}>{stats.topLinePct === null ? "—" : pct(stats.topLinePct, 1)} of option MV</span>
            </div>
            <CompactPositionStrip row={stats.topLine} contextLabel="Concentration" showReason={true} />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};


const DteBucketedLeapsSection: React.FC<{ rows: HeldLeapRow[]; columns?: any[] }> = ({ rows }) => {
  const groups = React.useMemo(() => {
    const byBucket = DTE_BUCKETS.map((bucket) => ({ bucket, rows: [] as HeldLeapRow[] }));
    for (const row of rows ?? []) {
      const bucket = dteBucketFor(row);
      const target = byBucket.find((g) => g.bucket.key === bucket.key) ?? byBucket[byBucket.length - 1];
      target.rows.push(row);
    }
    return byBucket.map((g) => buildDteGroup(g.bucket, g.rows));
  }, [rows]);

  const allGroup = React.useMemo(
    () =>
      buildDteGroup(
        {
          key: "all",
          shortLabel: "All",
          label: "All LEAPS",
          description: "Full option sleeve across all DTE buckets.",
          min: null,
          max: null,
          tone: "info",
        },
        rows ?? []
      ),
    [rows]
  );

  const materialGroups = groups.filter((g) => g.rows.length > 0);
  const agingMv = groups
    .filter((g) => g.bucket.key === "lt365" || g.bucket.key === "365_599")
    .reduce((sum, g) => sum + g.marketValue, 0);
  const totalMv = allGroup.marketValue;
  const agingPct = totalMv > 0 ? agingMv / totalMv : null;
  const maxBucketMv = Math.max(...groups.map((g) => g.marketValue), 1);
  const shortestRunwayRows = React.useMemo(() => sortPositionRows(rows ?? [], "dte"), [rows]);

  return (
    <Card style={themeCardStyle}>
      <CardContent className="space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>LEAPS Runway Map</div>
            <div className="text-xs" style={secondaryText}>
              DTE concentration, aging pressure, and the shortest runway watchlist without duplicating the full ledger.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded border px-2 py-1" style={inlineBoxStyle}>Option MV <strong>{dollars(totalMv, 0)}</strong></span>
            <span className="rounded border px-2 py-1" style={inlineBoxStyle}>Aging / mature <strong>{agingPct === null ? "—" : pct(agingPct, 1)}</strong></span>
            <span className="rounded border px-2 py-1" style={inlineBoxStyle}>Active buckets <strong>{materialGroups.length}</strong></span>
            <span className="rounded border px-2 py-1" style={inlineBoxStyle}>Avg DTE <strong>{allGroup.avgDte === null ? "—" : allGroup.avgDte.toFixed(0)}</strong></span>
          </div>
        </div>

        <div className="grid gap-2">
          {materialGroups.map((group) => {
            const width = group.marketValue > 0 ? clamp((group.marketValue / maxBucketMv) * 100, 4, 100) : 0;
            const retTone = group.ret !== null && group.ret >= 0 ? metricPositiveText : metricNegativeText;
            return (
              <div key={group.bucket.key} className="rounded border px-3 py-2" style={inlineBoxStyle}>
                <div className="grid items-center gap-3" style={{ gridTemplateColumns: "minmax(150px, 1fr) minmax(180px, 2fr) repeat(4, minmax(78px, auto))" }}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold" style={{ color: ui.textPrimary }}>{group.bucket.shortLabel}</span>
                      <span className="rounded px-2 py-0.5 text-xs" style={badgeStyle(group.bucket.tone)}>{group.rows.length}</span>
                    </div>
                    <div className="text-[11px]" style={smallMutedText}>{group.bucket.description}</div>
                  </div>
                  <div>
                    <div className="h-2 overflow-hidden rounded" style={{ background: "var(--bar-track)" }}>
                      <div className="h-2 rounded" style={{ width: `${width}%`, background: "var(--bar-fill)" }} />
                    </div>
                  </div>
                  <div className="text-xs">
                    <div style={smallMutedText}>MV</div>
                    <strong style={{ color: ui.textPrimary }}>{dollars(group.marketValue, 0)}</strong>
                  </div>
                  <div className="text-xs">
                    <div style={smallMutedText}>Avg DTE</div>
                    <strong style={{ color: ui.textPrimary }}>{group.avgDte === null ? "—" : group.avgDte.toFixed(0)}</strong>
                  </div>
                  <div className="text-xs">
                    <div style={smallMutedText}>Return</div>
                    <strong style={retTone}>{group.ret === null ? "—" : pct(group.ret, 1)}</strong>
                  </div>
                  <div className="text-xs">
                    <div style={smallMutedText}>Lines</div>
                    <strong style={{ color: ui.textPrimary }}>{group.rows.length}</strong>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded border p-3" style={sectionCardStyle}>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>Shortest Runway Watch</div>
              <div className="text-xs" style={smallMutedText}>Top DTE-sensitive lines only; full DTE sorting remains in Detailed LEAPS Ledger.</div>
            </div>
            <span className="rounded border px-2 py-0.5 text-xs" style={inlineBoxStyle}>{shortestRunwayRows.length} line{shortestRunwayRows.length === 1 ? "" : "s"}</span>
          </div>
          <CompactPositionList
            rows={shortestRunwayRows}
            maxRows={5}
            sortMode="dte"
            contextLabel="Runway"
            emptyText="No LEAPS positions returned for DTE review."
          />
        </div>
      </CardContent>
    </Card>
  );
};


type ExitWatchSummaryShape = {
  total: number;
  hard: number;
  peaked: number;
  topReasons: [string, number][];
};

/* ============================ Positions: one table, four lenses ==============
 *
 * WHAT THIS REPLACES. The same heldLeaps array was rendered FIVE ways on this page:
 * the Exit Review Queue (12 compact rows), the Shortest Runway Watch (5 rows x 22
 * facts), the Largest Concentration Line (13 tiles), the DTE bucket bars, and the
 * 24-column ledger. Four of the five were the same rows under a different filter, and
 * each carried a subtitle pointing at the ledger -- the file telling you to go
 * somewhere else, four separate times. They are now LENSES over one table.
 * (The DTE bars stay in Runway: those are an aggregate, not a rendering of rows.)
 *
 * THE HONESTY FIX THIS CARRIES. The "hard exit is not evaluated" caveat existed on
 * exactly ONE of the five -- and not on the 24-column ledger, the surface most likely
 * to be read. dbo.HeldLeaps.hard_sell HAS NO WRITER, so a missing Hard Exit badge
 * means the check never ran, not that the line was checked and found clear. The most
 * read table was the most reassuring one. The caveat now renders above EVERY lens,
 * because there is only one table left to put it on.
 *
 * TWO COLUMN SETS, because they answer different questions:
 *   Decision  -- what should I do about this line (posture, why now, exit rank and
 *                score side by side, hard exit, peaked/mature, drawdown from peak).
 *   Economics -- what is it worth and can I trade it (entry, market, value, return,
 *                bid/ask/spread/OI, greeks, IV, age).
 * Contract identity columns appear in both; nothing else is duplicated across them.
 */

type PositionLens = "all" | "flagged" | "runway" | "largest";
type PositionColumnSet = "decision" | "economics";

const POSITION_LENSES: Array<{ key: PositionLens; label: string; blurb: string }> = [
  { key: "all", label: "All lines", blurb: "Every held option line." },
  {
    key: "flagged",
    label: "Flagged for review",
    blurb: "Lines the exit model or the rules have flagged. Ranked by exit risk.",
  },
  {
    key: "runway",
    label: "Shortest runway",
    blurb: "Sorted by days to expiry, soonest first. Calendar time, not the deployment ladder.",
  },
  {
    key: "largest",
    label: "Largest lines",
    blurb: "Sorted by market value, biggest first. Concentration read.",
  },
];

/* Column ids per set. Identity (display/exp/strike/right/qty) is in both; every other
 * id appears in exactly one, so switching sets never just reshuffles the same columns. */
/* "display" is deliberately absent from both sets: it renders blank on held LEAPS rows,
 * which is exactly why the old ledger hid it (HIDE_HELD_COLS). Contract identity is
 * carried by exp + strike + right, as it was there. */
const DECISION_COLUMN_IDS = [
  "exp", "strike", "right", "qty", "dte",
  "decisionPosture", "decisionDetail", "hardExit", "hardExitReason",
  "likelyPeaked", "likelyMature", "rank", "exitHintScore", "exitHint",
  "ddFromPeak", "peakPct", "returnPct",
];

const ECONOMICS_COLUMN_IDS = [
  "exp", "strike", "right", "qty", "dte",
  "entry", "market", "value", "returnPct", "weightPct",
  "bid", "ask", "spreadPct", "openInterest",
  "delta", "gamma", "vega", "impliedVol", "ageDays",
];

const HARD_EXIT_CAVEAT =
  "Hard-exit is NOT evaluated by any producer - dbo.HeldLeaps.hard_sell has no writer. " +
  "The absence of a Hard exit badge means this check does not run, NOT that the position " +
  "was checked and found clear.";

const PositionsSection: React.FC<{
  rows: HeldLeapRow[];
  summary: ExitWatchSummaryShape;
  lastUpdatedUTC: string | null;
}> = ({ rows, summary, lastUpdatedUTC }) => {
  const [lens, setLens] = React.useState<PositionLens>("all");
  const [columnSet, setColumnSet] = React.useState<PositionColumnSet>("decision");

  const all = React.useMemo(() => rows ?? [], [rows]);

  const flagged = React.useMemo(
    () =>
      all.filter((r) => {
        const hard = (r as any).hardExit === true;
        const peaked = (r as any).likelyPeaked === true;
        const p = toNum((r as any).exitHintPercentile);
        const score = toNum((r as any).exitHintScore);
        if (hard || peaked) return true;
        if (p !== null && p >= 80) return true;
        if (score !== null && score >= 0.08) return true;
        return false;
      }),
    [all]
  );

  const lensRows = React.useMemo(() => {
    switch (lens) {
      case "flagged":
        return sortPositionRows(flagged, "risk");
      case "runway":
        return sortPositionRows(all, "dte");
      case "largest":
        return sortPositionRows(all, "value");
      default:
        return all;
    }
  }, [lens, all, flagged]);

  const columns = React.useMemo(() => {
    const wanted = columnSet === "decision" ? DECISION_COLUMN_IDS : ECONOMICS_COLUMN_IDS;
    const byId = new Map<string, any>();
    for (const col of heldColumns as any[]) {
      const id = (col?.id ?? col?.accessorKey ?? "").toString();
      if (id) byId.set(id, col);
    }
    // Ordered by the id list, not by the source array, so each set reads in its own
    // deliberate order rather than in whatever order the shared file happens to define.
    return wanted.map((id) => byId.get(id)).filter(Boolean);
  }, [columnSet]);

  const totalValue = React.useMemo(() => all.reduce((sum, r) => sum + leapMarketValue(r), 0), [all]);
  const activeLens = POSITION_LENSES.find((l) => l.key === lens);

  return (
    <Card style={themeCardStyle}>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>
              Positions
            </div>
            <div className="text-xs" style={secondaryText}>
              {activeLens?.blurb}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded border px-2 py-0.5" style={inlineBoxStyle}>
              {lensRows.length} of {all.length} line{all.length === 1 ? "" : "s"}
            </span>
            <span className="rounded border px-2 py-0.5" style={inlineBoxStyle}>
              MV <strong style={{ color: ui.textPrimary }}>{dollars(totalValue, 0)}</strong>
            </span>
            <span className="rounded px-2 py-0.5" style={badgeStyle(summary.total > 0 ? "warning" : "success")}>
              {summary.total > 0 ? `${summary.total} flagged` : "None flagged"}
            </span>
            {lastUpdatedUTC ? (
              <span className="rounded border px-2 py-0.5" style={{ ...inlineBoxStyle, color: ui.textSecondary }}>
                Exit hints <strong style={{ color: ui.textPrimary }}>{lastUpdatedUTC}</strong>
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1">
            {POSITION_LENSES.map((l) => {
              const on = l.key === lens;
              return (
                <button
                  key={l.key}
                  type="button"
                  onClick={() => setLens(l.key)}
                  aria-pressed={on}
                  className="rounded-full border px-3 py-1 text-xs font-medium transition"
                  style={
                    on
                      ? { ...badgeStyle("info"), borderColor: ui.cardBorder }
                      : { ...inlineBoxStyle, color: ui.textSecondary }
                  }
                >
                  {l.label}
                  {l.key === "flagged" && flagged.length > 0 ? ` (${flagged.length})` : ""}
                </button>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-1">
            <span className="text-[10px] uppercase tracking-wide" style={smallMutedText}>
              Columns
            </span>
            {(["decision", "economics"] as PositionColumnSet[]).map((cs) => {
              const on = cs === columnSet;
              return (
                <button
                  key={cs}
                  type="button"
                  onClick={() => setColumnSet(cs)}
                  aria-pressed={on}
                  className="rounded-full border px-3 py-1 text-xs font-medium capitalize transition"
                  style={
                    on
                      ? { ...badgeStyle("info"), borderColor: ui.cardBorder }
                      : { ...inlineBoxStyle, color: ui.textSecondary }
                  }
                >
                  {cs}
                </button>
              );
            })}
          </div>
        </div>

        {/* On EVERY lens, not just one of them. See the block comment above. */}
        <div className="rounded border px-3 py-2 text-[11px] leading-relaxed" style={inlineBoxStyle}>
          <strong style={{ color: ui.textPrimary }}>Hard-exit is not evaluated.</strong> No producer writes
          it, so an empty Hard Exit column means the check does not run — not that these lines were checked
          and found clear. Exit <em>rank</em> is a within-book ORDER (somebody is always 1st, even on a calm
          book); read the exit <em>score</em> beside it for severity.
        </div>

        {lensRows.length ? (
          // Keyed WRAPPER, not a key on DataTable itself: it is a generic function
          // component whose inferred props type rejects `key`. The remount is the point
          // -- DataTable seeds its sort state from initialSorting ONCE on mount and never
          // re-reads the prop, so without this the lens buttons changed the caption and
          // left the row order alone. Remounting also resets to page 1, which is right
          // when the rows change meaning.
          <React.Fragment key={lens}>
          <DataTable
            columns={columns}
            data={lensRows}
            Toolbar={undefined}
            Footer={undefined}
            initialSorting={
              lens === "flagged"
                ? [
                    { id: "hardExit", desc: true },
                    { id: "likelyPeaked", desc: true },
                    { id: "exitHintScore", desc: true },
                  ]
                : lens === "runway"
                ? [{ id: "dte", desc: false }]
                : lens === "largest"
                ? [{ id: "value", desc: true }]
                : [{ id: "exp", desc: false }]
            }
            pageSizeDefault={15}
            pageSizeOptions={[10, 15, 25, 50, 100, 200]}
            showColumnFilters={true}
          />
          </React.Fragment>
        ) : (
          <div className="rounded border px-3 py-3 text-xs" style={inlineBoxStyle}>
            {lens === "flagged"
              ? "No line is flagged for exit review. Note the caveat above: hard-exit is not among the checks that ran."
              : "No held option lines in this payload."}
          </div>
        )}

        {summary.topReasons.length > 0 && lens === "flagged" ? (
          <div className="flex flex-wrap gap-2 text-xs">
            {summary.topReasons.map(([reason, count]) => (
              <span key={reason} className="rounded border px-2 py-1" style={inlineBoxStyle} title={HARD_EXIT_CAVEAT}>
                {reason} <strong style={{ color: ui.textPrimary }}>{count}</strong>
              </span>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};


const RebalanceSellPreviewCompact: React.FC<{ orders: RebalanceOrderPreview[] }> = ({ orders }) => {
  const rows = Array.isArray(orders) ? orders : [];

  if (!rows.length) {
    return <div className="text-xs" style={smallMutedText}>No rebalance trims implied right now.</div>;
  }

  return (
    <div className="pt-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>Rebalance Sell Preview</div>
        <div className="text-xs" style={secondaryText}>{rows.length} line{rows.length === 1 ? "" : "s"}</div>
      </div>
      <div className="mt-2 grid gap-2">
        {rows.slice(0, 8).map((o: any, idx: number) => {
          const cid = o?.contract_id ?? o?.contractId ?? "—";
          const qty = toInt(o?.estimated_qty ?? o?.estimatedQty) ?? null;
          const notional = toNum(o?.target_notional ?? o?.targetNotional) ?? null;
          const reason = (o?.reason ?? "—").toString();
          const label = fmtContractLabel({
            symbol: (o?.symbol ?? null) as any,
            expiration: (o?.expiration ?? null) as any,
            right: (o?.right ?? null) as any,
            strike: (typeof o?.strike === "number" ? o.strike : toNum(o?.strike)) ?? null,
            displayName: (o?.display_name ?? o?.display ?? null) as any,
            contractId: cid,
            contractDesc: (o?.contractDesc ?? null) as any,
          });

          return (
            <div key={`${cid}-${idx}`} className="grid gap-2 rounded border px-3 py-2 text-sm md:grid-cols-[minmax(260px,1fr)_80px_120px_minmax(160px,1fr)] md:items-center" style={inlineBoxStyle}>
              <div className="font-semibold" style={{ color: ui.textPrimary }}>{label}</div>
              <div className="text-xs md:text-right"><span style={smallMutedText}>Qty </span><strong style={{ color: ui.textPrimary }}>{qty === null ? "—" : qty}</strong></div>
              <div className="text-xs md:text-right"><span style={smallMutedText}>Notional </span><strong style={{ color: ui.textPrimary }}>{notional === null ? "—" : dollars(notional, 0)}</strong></div>
              <div className="text-xs" style={smallMutedText}>{reason}</div>
            </div>
          );
        })}
        {rows.length > 8 ? <div className="text-xs" style={smallMutedText}>Showing top 8 of {rows.length} preview lines.</div> : null}
      </div>
    </div>
  );
};

/* Read-only mirror of what the engines have staged.
 *
 * The Executed / Cancelled buttons that used to live here moved to the Trade Desk on
 * 2026-08-27 (CIO decision Q5). They hit the same endpoint, but this page's POST omitted
 * actedQty, cioFilledQty, cioAvgPrice and revisitOn, and offered two decisions where the
 * Desk offers four -- so a disposition taken here wrote a permanently thinner ledger
 * entry than the identical click one page over. */
const StagedExitOrdersCompact: React.FC<{
  orders: PendingOrderRow[];
}> = ({ orders }) => {
  const rows = Array.isArray(orders) ? orders : [];

  if (!rows.length) {
    return <div className="mt-1 text-xs" style={smallMutedText}>No staged exit orders (SELL/ROLL/TRIM) returned in this payload.</div>;
  }

  return (
    <div className="mt-2 grid gap-2">
      {rows.slice(0, 10).map((o, idx) => {
        const qty = toInt(o.overrideQty ?? o.estimatedQty) ?? null;
        const notional = toNum(o.overrideNotional ?? o.targetNotional) ?? null;
        const updated = (o.updatedAt ?? o.createdAt ?? null) as any;
        const orderId = typeof o.orderId === "number" ? o.orderId : null;
        const contractLabel = fmtContractLabel({
          symbol: o.symbol,
          expiration: o.expiration,
          right: o.right,
          strike: o.strike ?? null,
          displayName: o.displayName,
          contractId: o.contractId ?? null,
          contractDesc: o.contractDesc ?? null,
        });

        return (
          <div key={o.orderId ?? idx} className="rounded border p-3" style={inlineBoxStyle}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-[280px] flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded px-2 py-0.5 text-xs" style={badgeStyle("warning")}>{(o.action ?? "—").toString()}</span>
                  <span className="text-xs" style={smallMutedText}>Order {orderId ?? "—"}</span>
                </div>
                <div className="mt-1 font-semibold" style={{ color: ui.textPrimary }}>{contractLabel}</div>
                <div className="mt-1 text-xs" style={smallMutedText}>{o.reason ?? "No reason returned"}</div>
              </div>
              <div className="grid min-w-[260px] grid-cols-2 gap-2 text-xs md:text-right">
                <div><div style={smallMutedText}>Qty</div><strong style={{ color: ui.textPrimary }}>{qty === null ? "—" : qty}</strong></div>
                <div><div style={smallMutedText}>Notional</div><strong style={{ color: ui.textPrimary }}>{notional === null ? "—" : dollars(notional, 0)}</strong></div>
                <div><div style={smallMutedText}>Status</div><strong style={{ color: ui.textPrimary }}>{(o.status ?? "—").toString()}</strong></div>
                <div><div style={smallMutedText}>Updated</div><strong style={{ color: ui.textPrimary }}>{fmtLocalDateTime(updated)}</strong></div>
              </div>
              {orderId !== null ? (
                <Link
                  to="/trading-desk"
                  className="self-start text-xs underline-offset-2 hover:underline"
                  style={{ color: ui.accentInfo }}
                >
                  Record on Trade Desk →
                </Link>
              ) : null}
            </div>
          </div>
        );
      })}
      {rows.length > 10 ? (
        <div className="text-xs" style={smallMutedText}>Showing top 10 staged exit orders. Use Trading Desk for the complete operational queue.</div>
      ) : null}
    </div>
  );
};

/* ---------------------- Headline cockpit: NAV hero + attention ---------------------- */
const HeroComposition: React.FC<{ label: string; usd: number; weight: number | null; sub?: string }> = ({
  label,
  usd,
  weight,
  sub,
}) => (
  <div className="flex items-baseline justify-between gap-3 rounded px-2.5 py-1.5" style={{ backgroundColor: ui.rowAlt }}>
    <div className="min-w-0">
      <div className="truncate text-xs font-medium" style={{ color: ui.textPrimary }}>
        {label}
      </div>
      {sub ? (
        <div className="text-[10px]" style={smallMutedText}>
          {sub}
        </div>
      ) : null}
    </div>
    <div className="text-right">
      <div className="text-sm font-semibold tabular-nums" style={{ color: ui.textPrimary }}>
        {weight === null ? "—" : pct(weight, 1)}
      </div>
      <div className="text-[10px] tabular-nums" style={smallMutedText}>
        {dollars(usd, 0)}
      </div>
    </div>
  </div>
);

const NavHeroCard: React.FC<{
  totalNav: number | null;
  rows: { label: string; usd: number }[];
  componentsLabel: string;
  sourceLine: string;
  navAsOf: string | null;
  navSource: string | null;
  marketDataAsOf: string | null;
  marketDataLabel: string;
  marketDataStale: boolean;
  peakLabel: string;
  peakSuspect: boolean;
  peakAsOf: string | null;
  actionState: ActionState;
  actionTone: Tone;
}> = ({
  totalNav,
  rows,
  componentsLabel,
  sourceLine,
  navAsOf,
  navSource,
  marketDataAsOf,
  marketDataLabel,
  marketDataStale,
  peakLabel,
  peakSuspect,
  peakAsOf,
  actionState,
  actionTone,
}) => {
  const nav = typeof totalNav === "number" && totalNav > 0 ? totalNav : null;
  const pctOf = (v: number) => (nav && nav > 0 ? v / nav : null);

  return (
    <Card style={themeCardStyle}>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide" style={smallMutedText}>
              Total NAV (now)
            </div>
            <div className="mt-0.5 text-3xl font-semibold tabular-nums" style={{ color: ui.textPrimary }}>
              {nav === null ? "—" : dollars(nav, 0)}
            </div>
          </div>
          <span className="rounded px-2 py-0.5 text-xs font-semibold" style={badgeStyle(actionTone)}>
            LEAPS {actionState}
          </span>
        </div>

        <div className="flex items-center justify-between px-1 text-[10px] uppercase tracking-wide" style={smallMutedText}>
          <span>Composition</span>
          <span title="Each row is that sleeve's market value divided by the reconciled Total NAV above — IBKR book plus off-broker bank cash plus accruals. The sizing weights further down this page divide by the broker book instead, which is a smaller number, so the LEAPS share here reads lower than the LEAPS share there. Both are correct; they answer different questions.">
            % of NAV (reconciled)
          </span>
        </div>

        <div className="grid gap-1.5">
          {rows.map((r) => (
            <HeroComposition key={r.label} label={r.label} usd={r.usd} weight={pctOf(r.usd)} />
          ))}
        </div>

        <div className="flex items-center justify-between border-t pt-2 text-[11px]" style={{ borderColor: ui.cardBorder }}>
          <span style={smallMutedText}>{componentsLabel}</span>
          <span className="font-semibold" style={{ color: ui.textPrimary }}>
            = {nav === null ? "—" : dollars(nav, 0)} {nav !== null ? "✓" : ""}
          </span>
        </div>

        <div className="text-[11px] leading-relaxed" style={smallMutedText}>
          {sourceLine}
        </div>

        {/* State the other denominator explicitly rather than leaving the reader to
            discover that the LEAPS % here and the LEAPS % below disagree. */}
        <div className="text-[11px] leading-relaxed" style={smallMutedText}>
          Sizing weights further down divide by the <strong style={{ color: ui.textSecondary }}>broker book</strong>, not
          this reconciled total, so the LEAPS share there reads higher. Cap enforcement is on funded cost basis —{" "}
          <Link to="/leaps" style={{ color: ui.accentInfo }} className="underline-offset-2 hover:underline">
            Signals
          </Link>{" "}
          is authoritative for sizing.
        </div>

        <div className="text-[11px] leading-relaxed" style={smallMutedText}>
          NAV as of <strong style={{ color: ui.textSecondary }}>{navAsOf ? fmtLocalDateTime(navAsOf) : "—"}</strong>
          {navSource ? ` (${String(navSource).toUpperCase()})` : ""}
          {" · "}prices/greeks{" "}
          {marketDataStale ? (
            <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold" style={badgeStyle("warning")}>
              {marketDataLabel}
            </span>
          ) : (
            <strong style={{ color: ui.textSecondary }}>{marketDataLabel}</strong>
          )}
          {marketDataAsOf ? <span> · {marketDataAsOf}</span> : null}
        </div>

        {/* The high-water-mark ANCHOR has a DIFFERENT clock from prices/greeks above. Since
            2026-09-02 the "% off peak" on every row is priced live against that anchor (same
            mark as Return%), so this stamp dates the anchor, not the giveback figure. It still
            matters, and only one way: a high the 13:30 MT job never recorded means the live
            giveback is UNDERSTATED. The per-row "Price updated" stamp attests the price fields
            only — bumped at 06:15 MT by the holdings sync, not by refresh_peak_returns. When
            that job crashed for ~13 days in Aug 2026 the rows showed frozen giveback beside a
            fresh timestamp. Anything other than "current" is badged: unknown must never read
            as healthy. */}
        <div className="text-[11px] leading-relaxed" style={smallMutedText}>
          Peak anchor (high-water mark){" "}
          {peakSuspect ? (
            <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold" style={badgeStyle("warning")}>
              {peakLabel}
            </span>
          ) : (
            <strong style={{ color: ui.textSecondary }}>{peakLabel}</strong>
          )}
          {peakAsOf ? <span> · as of {peakAsOf}</span> : null}
          <span> · giveback on each row is priced live against it</span>
          {/* Only the genuinely-stale states carry the caution. "awaiting_today" is one
              session old on purpose — today's run simply is not due yet — so warning there
              would train the eye to ignore the badge that matters. */}
          {peakSuspect ? (
            <span> · a high made since then is unknown here, so “% off peak” may be understated</span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

/* ------------------------- Audit checks (can I trust it?) ---------------------
 * Answers "can I trust the number above?" WITHOUT putting a second copy of that
 * number next to it. Every row is a state, not a figure.
 *
 * THREE CHECKS WERE DELIBERATELY NOT BUILT, after the payloads were audited. Each
 * would have rendered a reassuring tick from something that cannot go red, which is
 * the exact failure this rework exists to remove:
 *
 *  - "NAV composition ties out": near-tautological. The hero's "IBKR cash & residual"
 *    row is DEFINED as ibkr_live_nav − leaps − fi, so every broker-side mark mismatch
 *    is absorbed into it and can never surface as a plug; only the off-broker legs can
 *    break the tie, and only when the administrator feed is loaded. The plug row also
 *    only renders above max($1,000, 0.2% of NAV). Represented honestly here as the
 *    off-broker overlay row, which says what it actually measures.
 *  - "Broker sync succeeded": no honest source. latest_nav's ibkrSync.ok is hardcoded
 *    true inside its success branch and absent on failure, so a tick reading it can
 *    never go red; refresh_portfolio's top-level syncOk overwrites the inner flag that
 *    does go false, and its zero-positions branch returns neither — an IBKR outage
 *    returning 0 rows renders "Synced 0 positions" in green. Needs the backend to
 *    persist a real last-sync outcome first.
 *  - "Quote coverage %": the Mid COALESCE chain terminates at h.market_price, so mid is
 *    non-null for any row with a stored mark and a coverage % would read ~100% on a book
 *    with no live quotes. Replaced by a count of lines lacking a two-sided quote. The
 *    open-interest half is worse — it COALESCEs through dbo.OptionPrices, dead since
 *    2025 — so OI is not on this strip at all.
 *
 * Display only. Nothing here is investor-facing output; it is operator telemetry. */

type AuditState = "ok" | "warn" | "bad" | "unknown";

const AUDIT_TONE: Record<AuditState, Tone> = {
  ok: "success",
  warn: "warning",
  bad: "danger",
  unknown: "warning", // never neutral: "we could not check" is a finding, not a shrug
};

type AuditRow = { id: string; label: string; state: AuditState; verdict: string; detail: string };

const AuditChecksCard: React.FC<{ rows: AuditRow[] }> = ({ rows }) => {
  const worst = rows.some((r) => r.state === "bad")
    ? "bad"
    : rows.some((r) => r.state === "warn" || r.state === "unknown")
    ? "warn"
    : "ok";

  return (
    <Card style={themeCardStyle}>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>
              Audit checks
            </div>
            <div className="text-xs" style={secondaryText}>
              Can the number above be trusted? States only — the figures themselves live where they are owned.
            </div>
          </div>
          <span className="rounded px-2 py-0.5 text-xs font-semibold" style={badgeStyle(AUDIT_TONE[worst as AuditState])}>
            {worst === "ok" ? "All checks pass" : worst === "bad" ? "Attention" : "Check"}
          </span>
        </div>

        <div className="grid gap-1.5 md:grid-cols-2">
          {rows.map((r) => (
            <div
              key={r.id}
              className="flex items-start justify-between gap-3 rounded border px-2.5 py-1.5"
              style={inlineBoxStyle}
              title={r.detail}
            >
              <div className="min-w-0">
                <div className="text-xs font-medium" style={{ color: ui.textPrimary }}>
                  {r.label}
                </div>
                <div className="text-[10px] leading-snug" style={smallMutedText}>
                  {r.detail}
                </div>
              </div>
              <span className="shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold" style={badgeStyle(AUDIT_TONE[r.state])}>
                {r.verdict}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/* -------------------------------- Component ----------------------------------- */
const PortfolioView: React.FC = () => {
  const { userLoading } = useUser();

  const [data, setData] = React.useState<PortfolioPayload | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [statusMsg, setStatusMsg] = React.useState<string | null>(null);
  // M3 Step 1: "Broker NAV couldn't be read" note from the Sync response (amber; never an error).
  const [navNote, setNavNote] = React.useState<string | null>(null);
  const [statusType, setStatusType] = React.useState<"info" | "success" | "error">("info");
  const [latestNav, setLatestNav] = React.useState<LatestNavState>(null);
  // Freshest option-prices/greeks snapshot time (risk_updated_at_utc), for the
  // trading-session-aware freshness signal in the headline. Display-only.
  const [marketDataAsOfMs, setMarketDataAsOfMs] = React.useState<number | null>(null);

  // The high-water-mark anchor has its OWN clock and it is not the per-row "Price updated"
  // stamp. It is written by refresh_peak_returns at 13:30 MT, while HeldLeaps.UpdatedAt is
  // bumped at 06:15 MT by a different job. Since 2026-09-02 the row's peak_return /
  // drawdown_from_peak arrive already priced live against that anchor (backend
  // _derive_live_peak_state), so staleness here means "a newer high may be missing" —
  // giveback understated — not "the figure is frozen". When the peak job crashed for ~13
  // days in August 2026 the rows showed frozen giveback beside a fresh-looking timestamp.
  // `stale` is deliberately nullable: null means "could not determine", never OK.
  // Intraday NAV curve (dbo.NavIntraday via /risk/nav_intraday). Display-only. Defaults to
  // today's trading session; the arrows step whole days. Deliberately its own fetch rather
  // than part of the main payload — a missing curve must never delay or break the cockpit.
  const [navCurve, setNavCurve] = React.useState<NavIntradaySeries | null>(null);
  const [navCurveLoading, setNavCurveLoading] = React.useState<boolean>(false);
  const [navCurveDate, setNavCurveDate] = React.useState<string | null>(null);
  // Range views (2026-09-01, CIO request): 1D keeps the minute chart + day stepper;
  // 2D/5D are minute-mode across sessions (fill in as NavIntraday history accrues);
  // MTD/QTD/YTD/ITD are daily marks of record with today's live tail. Separate state
  // so switching ranges never disturbs the cached 1D curve.
  // Default 2D on open (2026-09-02, CIO request) — session-scoped, not persisted.
  const [navRange, setNavRange] = React.useState<string>("2d");
  const [navRangeSeries, setNavRangeSeries] = React.useState<NavRangeSeries | null>(null);
  const [navRangeLoading, setNavRangeLoading] = React.useState<boolean>(false);
  // Bumped by the `nav` SignalR push (below) when the displayed day is today's session,
  // so the curve grows in near-real-time — one refetch per broadcast minute, riding the
  // exact event that CREATES each mark. No polling, and it goes quiet with the market.
  const [navCurveRefresh, setNavCurveRefresh] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      setNavCurveLoading(true);
      try {
        const q = navCurveDate ? `?date=${encodeURIComponent(navCurveDate)}` : "";
        const res = await fetch(`${API_BASE_URL}/risk/nav_intraday${q}`, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as NavIntradaySeries;
        if (!cancelled) {
          setNavCurve(json);
          if (!navCurveDate && json?.date) setNavCurveDate(json.date);
        }
      } catch {
        // Advisory panel. A failure leaves the previous curve on screen rather than
        // blanking the cockpit; the chart states its own capture counts either way.
      } finally {
        if (!cancelled) setNavCurveLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [navCurveDate, navCurveRefresh]);

  const stepNavCurveDate = React.useCallback((days: number) => {
    setNavCurveDate((prev) => {
      const base = prev ? new Date(`${prev}T12:00:00Z`) : new Date();
      base.setUTCDate(base.getUTCDate() + days);
      return base.toISOString().slice(0, 10);
    });
  }, []);

  // Range fetch — same fail-soft posture as the 1D curve: a failure leaves the previous
  // range on screen; the chart states its own capture counts either way. Rides the same
  // SignalR bump so the live tail grows with the market.
  React.useEffect(() => {
    if (navRange === "1d") return;
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      setNavRangeLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/risk/nav_intraday?range=${encodeURIComponent(navRange)}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as NavRangeSeries;
        if (!cancelled) setNavRangeSeries(json);
      } catch {
        // Advisory panel; keep the previous view rather than blanking the cockpit.
      } finally {
        if (!cancelled) setNavRangeLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [navRange, navCurveRefresh]);


  const [peakFreshness, setPeakFreshness] = React.useState<PeakFreshness | null>(null);

  // Declared up here because this component returns early while loading, and hooks
  // cannot sit behind those returns. The section list is static; only the BADGES depend
  // on loaded data, and those are derived further down by a pure function.
  const drawers = useDrawerState(PORTFOLIO_SECTIONS);
  const [methodologyOpen, setMethodologyOpen] = React.useState(false);

  const portfolioAbort = React.useRef<AbortController | null>(null);
  const mounted = React.useRef<boolean>(false);
  const latestOverviewRef = React.useRef<Overview | null>(null);

  const fetchLatestNav = React.useCallback(async (signal?: AbortSignal) => {
    const normalize = normalizeNavPayload;

    try {
      const res = await fetch(`${API_BASE_URL}/risk/nav_latest`, { signal });
      if (!res.ok) throw new Error(`Failed to load latest NAV: ${res.status}`);
      const json = await res.json();
      if (!mounted.current) return;
      setLatestNav(normalize(json));
      return;
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      console.error(e);
    }

    try {
      const res = await fetch(`${API_BASE_URL}/get_portfolio_nav_latest`, { signal });
      if (!res.ok) throw new Error(`Failed to load latest NAV legacy: ${res.status}`);
      const json = await res.json();
      if (!mounted.current) return;
      setLatestNav(normalize(json));
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      console.error(e);
      if (!mounted.current) return;
      setLatestNav(null);
    }
  }, []);

  // SignalR live NAV push (read-only telemetry). The push updates the SAME
  // setLatestNav the on-load / manual-refresh fetch uses, so whichever fired most
  // recently wins and the cockpit NAV drifts live during market hours. The mount load
  // + "Refresh holdings" remain the automatic fallback if SignalR is unavailable.
  useSignalR(
    "nav",
    (payload: any) => {
      if (payload && typeof payload === "object" && mounted.current) {
        setLatestNav(normalizeNavPayload(payload));
        // Each push means a new intraday mark was just recorded (nav_broadcast writes it
        // right after this same broadcast). Refresh the curve if it is showing today —
        // today in US/Eastern terms, the trading-date basis the backend keys marks on.
        try {
          const easternToday = new Intl.DateTimeFormat("en-CA", {
            timeZone: "America/New_York",
          }).format(new Date());
          setNavCurveDate((current) => {
            if (!current || current === easternToday) setNavCurveRefresh((n) => n + 1);
            return current;
          });
        } catch {
          // Intl/timezone unavailable: skip the live refresh rather than guess a date.
        }
      }
    },
    { enabled: !userLoading }
  );

  const fetchPortfolio = React.useCallback(async () => {
    portfolioAbort.current?.abort();
    const ac = new AbortController();
    portfolioAbort.current = ac;

    setLoading(true);

    // All three loads start together; the page still paints in ONE setData below, so
    // no block can render a tie-out before its own reconciliation has resolved.
    // /risk/leaps_metrics reads SQL (v_HeldLeaps_Display + IbkrPositionsRaw), not the
    // live IBKR socket, so starting it speculatively adds no broker load.
    const metricsPromise = fetchLeapsMetricsRows(ac.signal).catch(() => [] as RiskMetricsRow[]);
    const navPromise = fetchLatestNav(ac.signal);

    try {
      const res = await fetch(`${API_BASE_URL}/get_portfolio_view`, { signal: ac.signal });
      if (!res.ok) throw new Error(`Failed to load portfolio: ${res.status}`);
      const json = await res.json();
      if (!mounted.current) return;

      const overview: Overview = json?.overview ?? {
        portfolioNAV: 0,
        vixZScore: null,
        vixCurrent: null,
        leapsTargetPct: 0,
        currentLeapsPct: 0,
        remainingNotionalToDeploy: 0,
        early_defense: null,
      };

      overview.leapsTargetPct = (toPctDecimal(overview.leapsTargetPct) ?? 0) as any;
      overview.currentLeapsPct = (toPctDecimal(overview.currentLeapsPct) ?? 0) as any;

      latestOverviewRef.current = overview;

      const heldSrc: any[] = Array.isArray(json?.heldLeaps)
        ? json.heldLeaps
        : Array.isArray(json?.heldLeapsRaw)
        ? json.heldLeapsRaw
        : [];

      // Freshest option-prices/greeks snapshot across positions (risk_updated_at_utc),
      // for the trading-session-aware freshness signal in the headline. Display-only.
      const freshestRiskMs = (heldSrc as any[]).reduce((mx: number, r: any) => {
        const raw = r?.risk_updated_at_utc ?? r?.RiskUpdatedAtUTC ?? r?.riskUpdatedAt ?? null;
        const t = raw ? Date.parse(String(raw)) : NaN;
        return Number.isFinite(t) && t > mx ? t : mx;
      }, 0);
      if (mounted.current) setMarketDataAsOfMs(freshestRiskMs > 0 ? freshestRiskMs : null);

      // Peak-state clock (dbo.HeldLeaps_MetricsDaily.AsOfDateUtc), emitted by the backend
      // as peakDataFreshness. Absent field => null => renders as "unknown", never as OK.
      if (mounted.current) {
        setPeakFreshness(parsePeakFreshness((json as any)?.peakDataFreshness));
      }

      // Book-integrity counts, off the same raw rows. See the BookIntegrity doc comment
      // for why each of these is a count of BAD lines rather than a coverage ratio.
      const integrity: BookIntegrity = (heldSrc as any[]).reduce(
        (acc: BookIntegrity, r: any) => {
          acc.lines += 1;

          const rawRisk = r?.risk_updated_at_utc ?? r?.RiskUpdatedAtUTC ?? r?.riskUpdatedAt ?? null;
          const t = rawRisk ? Date.parse(String(rawRisk)) : NaN;
          if (Number.isFinite(t)) {
            acc.oldestRiskMs = acc.oldestRiskMs === null ? t : Math.min(acc.oldestRiskMs, t);
          } else {
            acc.noRiskStamp += 1;
          }

          const bid = toNum(r?.bid ?? r?.Bid ?? r?.risk_bid);
          const ask = toNum(r?.ask ?? r?.Ask ?? r?.risk_ask);
          if (bid === null || ask === null) acc.noTwoSidedQuote += 1;

          const src = String(r?.risk_source ?? r?.RiskSource ?? "").toLowerCase();
          if (src === "missing") acc.noGreeks += 1;

          const entry = toNum(r?.entry_price ?? r?.entry ?? r?.EntryPrice ?? r?.AvgPrice);
          if (entry === null || entry === 0) {
            acc.noEntryPrice += 1;
            acc.noEntryPriceMv += leapMarketValue(r);
          }
          return acc;
        },
        { lines: 0, oldestRiskMs: null, noRiskStamp: 0, noTwoSidedQuote: 0, noGreeks: 0, noEntryPrice: 0, noEntryPriceMv: 0 }
      );

      const fiSrc: any[] = Array.isArray(json?.fixedIncome)
        ? json.fixedIncome
        : Array.isArray(json?.fixedIncomeRaw)
        ? json.fixedIncomeRaw
        : [];

      const fixedIncomeTargets: Record<string, number> | undefined =
        json?.fixedIncomeTargets && typeof json.fixedIncomeTargets === "object"
          ? json.fixedIncomeTargets
          : undefined;

      let heldLeaps: HeldLeapRow[] = heldSrc.map((r: any) => {
        if (typeof r?.contractId !== "undefined" || r?.display) {
          const exitHintPercentile = toNum(r.exitHintPercentile ?? r.exit_hint_percentile);
          const mlProb = toNum(r.mlProb ?? r.ml_prob);
          const scoreFromPercentile =
            typeof exitHintPercentile === "number" ? exitHintPercentile / 100 : null;

          const exitHintScore =
            toNum(r.exitHintScore) ?? mlProb ?? scoreFromPercentile ?? toNum(r.exit_hint_score);

          const strike = toNum(r.strike ?? r.strike_raw);
          const dte = toInt(r.dte ?? r.dte_raw);
          const qty = toNum(r.qty ?? r.quantity) ?? 0;
          const entry = toNum(r.entry ?? r.entry_price);
          const market = toNum(r.market ?? r.market_price);
          const value = toNum(r.value ?? r.market_value);
          const returnPct = toNum(r.returnPct ?? r.return_pct);
          const peakPct = toNum(r.peakPct ?? r.peak_return);
          const ddFromPeak = toNum(r.ddFromPeak ?? r.drawdown_from_peak);
          const ageDays = toInt(r.ageDays ?? r.age_days);
          const drawdownAbs = toNum(r.drawdownAbs ?? r.drawdown_abs);
          const ponrScore = toNum(r.ponrScore ?? r.ponr_score);

          const likelyMature =
            typeof (r.likelyMature ?? r.likely_mature) !== "undefined"
              ? Boolean(r.likelyMature ?? r.likely_mature)
              : undefined;

          const hardExit =
            typeof r.hardExit !== "undefined"
              ? Boolean(r.hardExit)
              : typeof r.hard_exit !== "undefined"
              ? Boolean(r.hard_exit)
              : undefined;

          const hardExitReason = (r.hardExitReason ?? r.hard_exit_reason ?? null) as string | null;

          return {
            contractId: String(r.contractId ?? r.contract_id ?? r.conid ?? ""),
            display: r.display ?? r.display_name ?? r.DisplayName ?? "",
            account: r.account ?? r.account_id ?? "",
            subAccount: r.subAccount ?? r.sub_account_id ?? undefined,
            accountClass:
              r.accountClass ?? (r.account_class as HeldLeapRow["accountClass"]) ?? undefined,
            strike: strike ?? undefined,
            right: r.right ?? r.right_raw ?? undefined,
            exp: r.exp ?? r.expiration ?? undefined,
            dte,
            qty,
            entry,
            market,
            value,
            returnPct,
            peakPct,
            ddFromPeak,
            likelyPeaked:
              typeof (r.likelyPeaked ?? r.likely_peaked) !== "undefined"
                ? Boolean(r.likelyPeaked ?? r.likely_peaked)
                : undefined,
            exitHintReason: r.exitHintReason ?? r.exit_hint_reason ?? undefined,
            exitHintScore: exitHintScore ?? null,
            exitHintPercentile: exitHintPercentile ?? null,
            exitHintRawScore: toNum(r.exit_hint_score) ?? toNum(r.exitHintScore) ?? null,
            exitRiskRankOf: toNum((r as any).exit_risk_rank_of) ?? null,
            ageDays,
            drawdownAbs,
            ponrScore,
            likelyMature,
            hardExit,
            hardExitReason,
            delta: toNum(r.delta ?? r.Delta) ?? null,
            gamma: toNum(r.gamma ?? r.Gamma) ?? null,
            gammaUnderlyingPrice: toNum(r.gammaUnderlyingPrice ?? r.gamma_underlying_price) ?? null,
            theta: toNum(r.theta ?? r.Theta) ?? null,
            vega: toNum(r.vega ?? r.Vega) ?? null,
            impliedVol: toNum(r.impliedVol ?? r.implied_vol ?? r.ImpliedVol) ?? null,
            bid: toNum(r.bid ?? r.Bid) ?? null,
            ask: toNum(r.ask ?? r.Ask) ?? null,
            mid: toNum(r.mid ?? r.Mid) ?? null,
            openInterest: toNum(r.openInterest ?? r.open_interest ?? r.OpenInterest) ?? null,
            decisionPosture: (r.decisionPosture ?? r.decision_posture ?? r.DecisionPosture ?? null) as string | null,
            decisionDetail: (r.decisionDetail ?? r.decision_detail ?? r.DecisionDetail ?? null) as string | null,
            decisionPriority: toNum(r.decisionPriority ?? r.decision_priority ?? r.DecisionPriority) ?? null,
            updatedAt: (r.updatedAt ?? r.UpdatedAt ?? null) as string | null,
          } as HeldLeapRow;
        }

        if (
          typeof (r as RawHeldLeapRow)?.conid !== "undefined" ||
          typeof (r as RawHeldLeapRow)?.contract_id !== "undefined"
        ) {
          const rr = r as RawHeldLeapRow;
          const percentile = toNum(rr.exit_hint_percentile);
          const mlProb = toNum(rr.ml_prob);
          const scoreFromPercentile = typeof percentile === "number" ? percentile / 100 : null;
          const exitHintScore = mlProb ?? scoreFromPercentile ?? toNum(rr.exit_hint_score);

          return {
            contractId: String(rr.contract_id ?? rr.conid ?? ""),
            display: rr.display_name ?? rr.DisplayName ?? "",
            account: rr.account_id ?? "",
            subAccount: rr.sub_account_id ?? undefined,
            accountClass: (rr.account_class ?? undefined) as HeldLeapRow["accountClass"],
            strike: toNum(rr.strike) ?? undefined,
            right: rr.right ?? undefined,
            exp: rr.expiration ?? undefined,
            dte: toInt(rr.dte),
            qty: toNum(rr.quantity) ?? 0,
            // Lot-size constraint on the 2x harvest. Backend sets this for EVERY held row
            // from quantity (strategy/portfolio_view.py), not just for positions already
            // past the trigger — which is why it used to render on zero rows.
            singleContractConstraint: rr.home_run_single_contract_constraint === true,
            minContractsForTrim: toInt(rr.home_run_min_contracts_for_trim) ?? undefined,
            entry: toNum(rr.entry_price) ?? undefined,
            market: toNum(rr.market_price) ?? undefined,
            value: toNum(rr.market_value) ?? undefined,
            returnPct: toNum(rr.return_pct),
            peakPct: toNum(rr.peak_return),
            ddFromPeak: toNum(rr.drawdown_from_peak),
            likelyPeaked:
              typeof rr.likely_peaked === "number"
                ? rr.likely_peaked === 1
                : rr.likely_peaked === true
                ? true
                : undefined,
            exitHintReason: rr.exit_hint_reason ?? undefined,
            exitHintScore: exitHintScore ?? null,
            exitHintPercentile: percentile ?? null,
            exitHintRawScore: toNum(rr.exit_hint_score) ?? null,
            exitRiskRankOf: toNum((rr as any).exit_risk_rank_of) ?? null,
            ageDays: toInt(rr.age_days),
            drawdownAbs: toNum(rr.drawdown_abs),
            ponrScore: toNum(rr.ponr_score),
            likelyMature:
              typeof rr.likely_mature === "number"
                ? rr.likely_mature === 1
                : rr.likely_mature === true
                ? true
                : undefined,
            hardExit:
              typeof rr.hard_exit === "number"
                ? rr.hard_exit === 1
                : rr.hard_exit === true
                ? true
                : undefined,
            hardExitReason: rr.hard_exit_reason ?? null,
            delta: toNum(rr.delta) ?? null,
            gamma: toNum(rr.gamma) ?? null,
            gammaUnderlyingPrice: toNum(rr.gamma_underlying_price) ?? null,
            theta: toNum(rr.theta) ?? null,
            vega: toNum(rr.vega) ?? null,
            impliedVol: toNum(rr.implied_vol) ?? null,
            bid: toNum(rr.bid) ?? null,
            ask: toNum(rr.ask) ?? null,
            mid: toNum(rr.mid) ?? null,
            openInterest: toNum(rr.open_interest) ?? null,
            decisionPosture: (rr.decision_posture ?? null) as string | null,
            decisionDetail: (rr.decision_detail ?? null) as string | null,
            decisionPriority: toNum(rr.decision_priority) ?? null,
            updatedAt: (rr.UpdatedAt ?? null) as string | null,
          } as HeldLeapRow;
        }

        const p = r as RawHeldLeapRowPascal;
        return {
          contractId: String(p.Conid ?? ""),
          display: p.DisplayName ?? "",
          account: p.AccountId ?? "",
          subAccount: p.SubAccountId ?? undefined,
          strike: toNum(p.Strike) ?? undefined,
          right: (p.Right as any) ?? undefined,
          exp: p.Expiration ?? undefined,
          dte: toInt(p.DTE),
          qty: toNum(p.Quantity) ?? 0,
          entry: toNum(p.AvgPrice) ?? undefined,
          market: toNum(p.LastPrice) ?? undefined,
          value: toNum(p.MarketValue) ?? undefined,
          returnPct:
            typeof p.AvgPrice === "number" && p.AvgPrice
              ? (toNum(p.LastPrice) ?? 0) / (toNum(p.AvgPrice) ?? 1) - 1
              : null,
          peakPct: null,
          ddFromPeak: toNum(p.DrawdownFromPeak) ?? null,
          likelyPeaked: typeof p.LikelyPeaked === "boolean" ? p.LikelyPeaked : undefined,
          exitHintReason: p.ExitHintReason ?? undefined,
          exitHintScore: toNum(p.ExitHintScore),
          exitHintPercentile: toNum(p.ExitHintPercentile),
          exitHintRawScore: toNum(p.ExitHintScore) ?? null,
          ageDays: toInt(p.AgeDays),
          drawdownAbs: toNum(p.DrawdownAbs),
          ponrScore: null,
          likelyMature: undefined,
          hardExit: (r as any)?.hardExit ?? undefined,
          hardExitReason: (r as any)?.hardExitReason ?? null,
          delta: toNum(p.Delta) ?? null,
          gamma: toNum(p.Gamma) ?? null,
          theta: toNum(p.Theta) ?? null,
          vega: toNum(p.Vega) ?? null,
          impliedVol: toNum(p.ImpliedVol) ?? null,
          bid: toNum((r as any)?.Bid ?? null),
          ask: toNum((r as any)?.Ask ?? null),
          mid: toNum((r as any)?.Mid ?? null),
          openInterest: toNum((r as any)?.OpenInterest ?? null),
          decisionPosture: ((r as any)?.DecisionPosture ?? null) as string | null,
          decisionDetail: ((r as any)?.DecisionDetail ?? null) as string | null,
          decisionPriority: toNum((r as any)?.DecisionPriority ?? null),
          updatedAt: ((r as any)?.UpdatedAt ?? (r as any)?.updatedAt ?? null) as string | null,
        } as HeldLeapRow;
      });

      // Started above, in parallel with this response. Still a no-op when no row
      // is missing greeks/quotes, exactly as the old conditional fetch was.
      if (heldLeaps.length && heldLeaps.some(rowNeedsRiskHydration)) {
        heldLeaps = mergeRiskMetricsIntoHeldLeaps(heldLeaps, await metricsPromise);
      }

      const fixedIncome: FixedIncomeRow[] = (fiSrc as any[]).map((r: any) => {
        if (typeof r?.ticker !== "undefined") {
          const base: FixedIncomeRow = {
            ticker: r.ticker,
            account: r.account ?? undefined,
            // Basis: get_portfolio_view emits weightPct as a plain FI-sleeve fraction
            // (0..1), or null when unknown — read it as-is. Deliberately not toPctDecimal:
            // its (1.5, 100] rescale heuristic would launder a bad feed into a
            // plausible-looking weight instead of letting it read as obviously broken.
            weightPct: toNum(r.weightPct) ?? null,
            value: toNum(r.value) ?? undefined,
          };

          const targetPct: number | null =
            toPctDecimal(r.targetPct) ?? toPctDecimal((r as any).target_pct);

          if (typeof targetPct === "number" && typeof base.weightPct === "number") {
            (base as any).targetPct = targetPct;
            (base as any).deltaPct = targetPct - (base.weightPct as number);
          }

          return base;
        }

        if (
          typeof (r as RawFixedIncomeRow)?.symbol !== "undefined" ||
          typeof (r as RawFixedIncomeRow)?.DisplayName !== "undefined"
        ) {
          const ticker = r.symbol ?? r.DisplayName ?? "";
          const weightPct = toPctDecimal(r.weight_pct) ?? toPctDecimal(r.weightpct);
          return {
            ticker,
            account: r.account_id ?? undefined,
            weightPct: weightPct ?? null,
            value: toNum(r.market_value) ?? undefined,
          };
        }

        const p = r as RawFixedIncomeRowPascal;
        return {
          ticker: (p.Ticker ?? "").toString(),
          account: undefined,
          weightPct: null,
          value: toNum(p.MarketValue) ?? undefined,
        };
      });

      await navPromise;

      const payload: PortfolioPayload = {
        overview,
        warnings: Array.isArray(json?.warnings) ? (json.warnings as string[]) : [],
        integrity,
        heldLeaps,
        fixedIncome,
        fixedIncomeTargets,
        rebalance: normalizeRebalanceMeta(json?.rebalance),
        rebalanceOrdersPreview: normalizeRebalanceOrdersPreview(
          json?.rebalanceOrdersPreview ?? json?.orders_preview ?? []
        ),
        pendingOrders: normalizePendingOrders(json?.pendingOrders ?? []),
        homeRun: json?.homeRun ?? undefined,
      };

      setData(payload);
      setStatusMsg(null);
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      console.error(e);
      if (!mounted.current) return;
      setStatusType("error");
      setStatusMsg("Failed to load portfolio view.");
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [fetchLatestNav]);

  React.useEffect(() => {
    mounted.current = true;
    if (!userLoading) {
      fetchPortfolio();
    }
    return () => {
      mounted.current = false;
      portfolioAbort.current?.abort();
    };
  }, [userLoading, fetchPortfolio]);

  const held = React.useMemo(() => data?.heldLeaps ?? [], [data?.heldLeaps]);
  const fixedIncome = React.useMemo(() => data?.fixedIncome ?? [], [data?.fixedIncome]);
  const homeRun = React.useMemo(() => data?.homeRun ?? null, [data?.homeRun]);
  const homeRunCandidates = React.useMemo(() => homeRun?.candidates ?? [], [homeRun?.candidates]);

  const exitHintsLastUpdatedUTC = React.useMemo(() => {
    const rows = (held as HeldLeapRowX[]) ?? [];
    const dates = rows
      .map((r) => (r.updatedAt ? parseUtc(r.updatedAt)?.getTime() ?? NaN : NaN))
      .filter((t) => Number.isFinite(t));
    if (!dates.length) return null;
    return fmtLocalDateTime(new Date(Math.max(...dates)));
  }, [held]);

  // D4: the SHARED gate, so this page and Risk flag the same lines. buildWatchlist keys
  // off the model's posture, includes home-run candidates, and uses percentile only as a
  // fallback when there is no posture -- it never gates on the raw exit score, which the
  // library documents as scaled drawdown with a whole-book floor near 0.13.
  const exitWatchlistKeys = React.useMemo(() => {
    const set = new Set<string>();
    for (const w of buildWatchlist(held as any[])) {
      const k = normalizeConidKey((w as any).contractId ?? (w as any).conid);
      if (k) set.add(k);
    }
    return set;
  }, [held]);

  // Kept as HeldLeapRow so the existing summary/table code is unchanged; membership is
  // decided by the shared gate above, not re-derived here.
  const exitWatchlist = React.useMemo(
    () =>
      ((held as HeldLeapRowX[]) ?? []).filter((r) => {
        const k = normalizeConidKey((r as any).contractId);
        return k ? exitWatchlistKeys.has(k) : false;
      }),
    [held, exitWatchlistKeys]
  );

  const exitWatchSummary = React.useMemo(() => {
    const rows = exitWatchlist as HeldLeapRowX[];
    const hard = rows.filter((r) => (r as any).hardExit === true).length;
    const peaked = rows.filter((r) => (r as any).likelyPeaked === true).length;
    const reasons: Record<string, number> = {};

    for (const r of rows) {
      const reason = ((r as any).hardExitReason || (r as any).exitHintReason || "")
        .toString()
        .trim();
      if (!reason) continue;
      reasons[reason] = (reasons[reason] ?? 0) + 1;
    }

    const topReasons = Object.entries(reasons)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);

    return { total: rows.length, hard, peaked, topReasons };
  }, [exitWatchlist]);

  const HIDE_HELD_COLS = React.useMemo(() => new Set(["drawdownAbs", "ponrScore", "display"]), []);
  const heldColumnsFiltered = React.useMemo(
    () =>
      heldColumns.filter((col: any) => {
        const id = (col?.id ?? col?.accessorKey ?? "").toString();
        if (HIDE_HELD_COLS.has(id)) return false;

        const headerTxt = typeof col?.header === "string" ? col.header : String(col?.header ?? "");
        const looksLikeDDAbs = /dd\s*abs/i.test(headerTxt) || /drawdown\s*abs/i.test(headerTxt);
        const looksLikePonr = /\bponr\b/i.test(headerTxt);
        return !(looksLikeDDAbs || looksLikePonr);
      }),
    [HIDE_HELD_COLS]
  );

  const pending = React.useMemo(
    () => (Array.isArray(data?.pendingOrders) ? data!.pendingOrders! : []),
    [data?.pendingOrders]
  );
  const pendingSummary = React.useMemo(() => summarizePendingOrders(pending), [pending]);

  const stagedExitOrders = React.useMemo(() => {
    const rows = pending ?? [];
    return rows
      .filter((o) => {
        const a = (o.action || "").toString().toUpperCase();
        const s = (o.status || "PENDING").toString().toUpperCase();
        const isExit = a === "SELL" || a === "ROLL" || a === "TRIM";
        const isPending = s === "" || s.includes("PENDING") || s.includes("STAGED");
        return isExit && isPending;
      })
      .slice(0, 50);
  }, [pending]);

  const handleRefreshHoldings = async () => {
    setRefreshing(true);
    setStatusType("info");
    setStatusMsg("Refreshing holdings from IBKR…");
    setNavNote(null);

    try {
      const res = await fetch(`${API_BASE_URL}/refresh_portfolio`, { method: "GET" });
      if (!res.ok) throw new Error(`Refresh failed: ${res.status}`);
      const json: RefreshPortfolioResponse = await res.json();

      // refresh_portfolio returns HTTP 200 even when the IBKR sync fails (it degrades to
      // last-known data). Do NOT show a green "synced" success on a failed sync — key off
      // the explicit syncOk / sync.error the backend now returns (2026-07-01 audit P1 #24).
      const syncFailed = json?.syncOk === false || Boolean(json?.sync?.error);
      if (syncFailed) {
        const detail = json?.sync?.error || (Array.isArray(json?.errors) ? json.errors.join("; ") : "IBKR sync failed");
        setStatusType("error");
        setStatusMsg(`⚠ IBKR sync failed — showing last-known holdings, not fresh data. ${detail}`);
      } else {
        const syncedRaw = json?.synced ?? json?.synced_positions;
        const synced = Number(syncedRaw);
        setStatusType("success");
        // M3 Step 1: a Sync refreshes holdings only; the official daily NAV is the scheduled jobs'.
        setStatusMsg(`✅ ${syncSuccessMessage(Number.isFinite(synced) && syncedRaw != null ? synced : null)}`);
      }
      // Amber, separate from the status, and ONLY on a successful sync (as on Signals / Fixed
      // Income): a failed sync shows red alone, never red and amber together.
      setNavNote(successNavNote(json));

      await fetchPortfolio();
    } catch (err: any) {
      console.error(err);
      setStatusType("error");
      setStatusMsg(`❌ Refresh failed: ${err.message || err.toString()}`);
    } finally {
      setRefreshing(false);
    }
  };

  if (userLoading) {
    return (
      <Layout>
        <div className="p-4" style={{ color: ui.textPrimary }}>
          Loading portfolio view…
        </div>
      </Layout>
    );
  }

  if (loading && !data) {
    return (
      <Layout>
        <div className="p-4" style={{ color: ui.textPrimary }}>
          Loading portfolio view…
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="p-4">
          <div className="mb-3 rounded px-3 py-2 text-sm" style={badgeStyle("danger")}>
            Unable to load portfolio data right now.
          </div>
          <HelionButton size="sm" onClick={fetchPortfolio}>
            Retry
          </HelionButton>
        </div>
      </Layout>
    );
  }

  const reb = data.rebalance ?? null;

  const sleeveTarget =
    (typeof reb?.target_pct_effective === "number" ? reb.target_pct_effective : null) ??
    (typeof reb?.target_pct_capped === "number" ? reb.target_pct_capped : null) ??
    toPctDecimal(data.overview.leapsTargetPct) ??
    0;

  const sleeveCurrent =
    (typeof reb?.current_weight === "number" ? reb.current_weight : null) ??
    toPctDecimal(data.overview.currentLeapsPct) ??
    0;

  const rebOrders = Array.isArray(data.rebalanceOrdersPreview) ? data.rebalanceOrdersPreview : [];

  const rebTargetCapped =
    typeof reb?.target_pct_effective === "number"
      ? reb.target_pct_effective
      : typeof reb?.target_pct_capped === "number"
      ? reb.target_pct_capped
      : null;

  const rebCurrentW = typeof reb?.current_weight === "number" ? reb.current_weight : null;

  const observedCash =
    typeof latestNav?.cash_override?.observed_cash === "number"
      ? latestNav.cash_override.observed_cash
      : typeof latestNav?.cash_nav === "number"
      ? latestNav.cash_nav
      : null;

  const reservedCash =
    typeof latestNav?.cash_override?.reserved_cash === "number"
      ? latestNav.cash_override.reserved_cash
      : observedCash;

  const cashOverrideEnabled = !!(
    latestNav?.cash_override && (
      latestNav.cash_override.enabled === true ||
      latestNav.cash_override.mode === "reserved" ||
      latestNav.cash_override.mode === "constrained" ||
      (typeof reservedCash === "number" && reservedCash > 0)
    )
  );

  const canonicalTotalNav =
    typeof latestNav?.total_nav === "number" && latestNav.total_nav > 0
      ? latestNav.total_nav
      : typeof data.overview.portfolioNAV === "number" && data.overview.portfolioNAV > 0
      ? data.overview.portfolioNAV
      : null;

  // Client-side fallback ONLY. Hardcodes the multiplier at 100, cannot see the stored
  // cost_basis, and counts a missing entry price as $0 cost -- all three understate the
  // funded basis, which makes the sleeve look further from the 40% purchase cap than it
  // is. Used only when the engine did not publish its figure, and labelled as such.
  const fundedLeapsCostFallback = (held as any[]).reduce((sum, r) => {
    const entry = typeof r.entry === "number" && isFinite(r.entry) ? r.entry : 0;
    const qty = typeof r.qty === "number" && isFinite(r.qty) ? r.qty : 0;
    return sum + entry * qty * 100;
  }, 0);

  // D2: prefer the ENGINE's funded_weight, the same field Risk (RiskDashboard engineFunded)
  // and Signals already read, so the three pages cannot quote different percentages
  // against the same cap. The engine sums cost_basis and falls back to
  // entry_price * quantity * multiplier -- see _fetch_funded_leaps_cost.
  const engineFundedWeight =
    typeof reb?.funded_weight === "number" && isFinite(reb.funded_weight) ? reb.funded_weight : null;
  const fundedBasisIsEngine = engineFundedWeight !== null;
  const sleeveFundedCurrent =
    engineFundedWeight !== null
      ? engineFundedWeight
      : typeof canonicalTotalNav === "number" && canonicalTotalNav > 0
      ? fundedLeapsCostFallback / canonicalTotalNav
      : null;

  const cashPctOfNav =
    typeof reservedCash === "number" && typeof canonicalTotalNav === "number" && canonicalTotalNav > 0
      ? reservedCash / canonicalTotalNav
      : null;

  const fiActualTotalNavPct =
    typeof canonicalTotalNav === "number" && canonicalTotalNav > 0
      ? fixedIncome.reduce((sum, r) => sum + (typeof r.value === "number" ? r.value : 0), 0) / canonicalTotalNav
      : null;


  const suggestedTrim =
    typeof reb?.suggested_trim_to_target === "number"
      ? reb.suggested_trim_to_target
      : typeof reb?.hard_cap_trim === "number" && (reb.hard_cap_trim ?? 0) > 0
      ? reb.hard_cap_trim
      : typeof reb?.trim_notional === "number"
      ? reb.trim_notional
      : null;

  const action = computeAllocationAction({
    targetPct: typeof rebTargetCapped === "number" ? rebTargetCapped : toPctDecimal(sleeveTarget),
    currentPct: typeof rebCurrentW === "number" ? rebCurrentW : toPctDecimal(sleeveCurrent),
    portfolioNav:
      typeof data.overview.portfolioNAV === "number" && isFinite(data.overview.portfolioNAV)
        ? data.overview.portfolioNAV
        : null,
    tolerancePct: reb?.rebalance_tolerance_pct ?? null,
  });

  const actionTone: Tone =
    action.state === "TRIM" ? "danger" : action.state === "ADD" ? "info" : "success";

  // Cap is measured on FUNDED / cost basis (CIO decision): market-value drift above
  // target is benign appreciation UNLESS the funded basis breaches the cap or the
  // regime target drops. Gate the actionable-trim display on that — market weight
  // alone never triggers a "trim" read here. (The trim MATH stays in the deferred
  // rebalance engine; this is display-only.)
  const fundedCapPort =
    typeof reb?.caps?.funded_max === "number" && isFinite(reb.caps.funded_max) ? reb.caps.funded_max : 0.4;
  const fundedCapBreachedPort =
    typeof sleeveFundedCurrent === "number" ? sleeveFundedCurrent > fundedCapPort + 0.005 : false;
  // A cap breach is NOT an executable trim. This is the same `if (fundedCapBreached)`
  // clause Signals removed from targetBasedDeriskRequired on 2026-08-13, which was never
  // carried here. CIO policy, verbatim 2026-08-27: the 40% funded/cost basis is a
  // PURCHASE constraint -- "we buy funded up to 40% but let it run" -- and "only regime
  // or axis grind trigger tell us to change the allocation down".
  //
  // Fixing the playbook copy alone would have made this worse, not better: the page would
  // have said "no further funded buying, this is not a trim signal" in amber while a red
  // TRIM chip and a suggested trim dollar figure sat beside it.
  //
  // The ENGINE's staged-trim verdict is the authority when it ran (P2-5, 2026-11-03): a
  // confirmed defense in Sweet-Spot trims to the 40% target, which equals the cap, so the
  // old target-drop test called that staged sell "AT CAP / not a trim". See
  // lib/portfolioTrimState.
  // "Engine unavailable" is NOT the same as "no trim". The fallback path runs on
  // weekends, holidays and stale-feed blocks and deliberately evaluates nothing, so a
  // dash there must not read as a clean bill of health. (backend 47e3d1b3)
  // Positive assertion only: the engine counts as having run ONLY when the payload says so
  // (backend fix/portfolio-view-engine-source). A missing rebalance block or missing markers is
  // "engine unavailable", never "the engine looked and found no trim" (review P2-5).
  const rebalanceEngineUnavailable = !(reb?.rebalance_source === "engine" && reb?.rebalance_engine_available === true);
  const trimState = resolvePortfolioTrimState({
    actionState: action.state,
    targetPct: typeof rebTargetCapped === "number" ? rebTargetCapped : null,
    fundedCap: fundedCapPort,
    fundedCapBreached: fundedCapBreachedPort,
    engineUnavailable: rebalanceEngineUnavailable,
    trimReason: reb?.trim_reason ?? null,
    engineTrimAmount: typeof suggestedTrim === "number" && isFinite(suggestedTrim) ? suggestedTrim : null,
  });
  const { executableTrim, atDeploymentCap, benignDrift, gateNotConfirmed, unevaluatedOver, dispActionState } = trimState;
  const dispActionTone: Tone = executableTrim
    ? "danger"
    : atDeploymentCap || benignDrift || gateNotConfirmed || unevaluatedOver
    ? "warning"
    : actionTone;

  const trimPreviewNumber =
    typeof suggestedTrim === "number" && isFinite(suggestedTrim)
      ? suggestedTrim
      : typeof action.trimNotional === "number" && isFinite(action.trimNotional)
      ? action.trimNotional
      : null;

  const trimPreviewTxt = rebalanceEngineUnavailable
    ? "engine unavailable"
    : executableTrim && typeof trimPreviewNumber === "number"
      ? dollars(trimPreviewNumber)
      : "—";

  // Reason shown in the Trim preview block. Gated on executableTrim so a market-
  // value drift above target (benign appreciation, funded basis under cap) never
  // surfaces a "cap_enforcement" / target trim reason. The raw engine reason can
  // be market-based on the fallback meta; we only show it when a funded-basis
  // breach or a regime target drop actually makes a trim executable.
  const trimReasonTxt = rebalanceEngineUnavailable
    ? (reb?.rebalance_note
        ? String(reb.rebalance_note)
        : "Rebalance engine did not run (weekend, holiday, or stale-feed block) — no trim was evaluated.")
    : executableTrim
    ? reb?.trim_reason
      ? String(reb.trim_reason)
      : "—"
    : gateNotConfirmed
    ? "target cut, but the sell gate is not confirmed — no trim staged"
    : atDeploymentCap
    ? "at deployment cap — no further funded buying, not a trim"
    : benignDrift
    ? "appreciation drift — no trim"
    : "—";

  // ---- Headline cockpit inputs (reuse existing derived values; no recompute) ----
  const leapsMv = (held as any[]).reduce((sum, r) => sum + leapMarketValue(r), 0);
  const fiMv = (fixedIncome as any[]).reduce(
    (sum, r) => sum + (typeof r.value === "number" && isFinite(r.value) ? r.value : 0),
    0
  );

  // Headline Total NAV = canonical RECONCILED figure (IBKR book + off-broker bank cash
  // + accruals from NAV Fund Services) — the same authoritative number the Risk/Signals
  // cockpits show (latestNav.reconciliation.daily.estimated_nav_now). Falls back to the
  // broker book if the administrator feed isn't loaded.
  // NOTE: canonicalTotalNav (broker book) intentionally STAYS the sizing denominator for
  // LEAPS weight / funded basis / cash% — only the headline + composition use the reconciled total.
  const navDaily: any = (latestNav as any)?.reconciliation?.daily ?? null;
  const reconciledNav =
    typeof navDaily?.estimated_nav_now === "number" && navDaily.estimated_nav_now > 0
      ? (navDaily.estimated_nav_now as number)
      : null;
  const headlineNav = reconciledNav ?? canonicalTotalNav;
  // IBKR book residual = live IBKR NAV − marked sleeve MVs. Captures IBKR cash plus any mark/snapshot
  // timing difference between the per-sleeve marks and the aggregate NAV feed, so LEAPS + FI + residual
  // ties exactly to the IBKR live NAV and the whole composition sums to the headline WITHOUT a
  // misleading "Other / unmodeled" plug. (The old row used the discretionary reserved-cash figure,
  // which double-counted vs the sleeve marks and left a spurious ~$160k plug.) Falls back to reserved
  // cash only when the live IBKR NAV isn't loaded.
  const ibkrLiveForHero = typeof navDaily?.ibkr_live_nav === "number" ? (navDaily.ibkr_live_nav as number) : null;
  const ibkrCashUsd =
    ibkrLiveForHero !== null
      ? ibkrLiveForHero - (leapsMv || 0) - (fiMv || 0)
      : typeof reservedCash === "number"
      ? reservedCash
      : 0;
  const bankCashUsd = typeof navDaily?.bank_cash === "number" ? (navDaily.bank_cash as number) : null;
  const accrualsUsd = typeof navDaily?.accruals_est_usd === "number" ? (navDaily.accruals_est_usd as number) : null;
  // When the administrator's off-broker cash feed isn't contemporaneous with the strike (month-end
  // close lag or a mid-month subscription+deployment), the bank/accruals SPLIT is unreliable — the
  // backend withholds accruals and flags overlay_reliable=false. In that case we do NOT itemize the
  // stale bank / garbage accruals (which would fire a spurious "Other / unmodeled" plug); we show a
  // single carried strike overlay row instead, which ties the composition back to the headline.
  const overlayReliable = navDaily?.overlay_reliable !== false; // default true when field absent (older payloads)
  const overlayUsd = typeof navDaily?.admin_overlay_usd === "number" ? (navDaily.admin_overlay_usd as number) : null;

  const heroRows: { label: string; usd: number }[] = [
    { label: "LEAPS (options)", usd: leapsMv || 0 },
    { label: "Fixed income", usd: fiMv || 0 },
    { label: ibkrLiveForHero !== null ? "IBKR cash & residual" : "Cash (IBKR)", usd: ibkrCashUsd },
  ];
  if (reconciledNav !== null && overlayReliable && bankCashUsd !== null)
    heroRows.push({ label: "Bank cash (off-broker)", usd: bankCashUsd });
  if (reconciledNav !== null && overlayReliable && accrualsUsd !== null)
    heroRows.push({ label: "Accruals", usd: accrualsUsd });
  if (reconciledNav !== null && !overlayReliable && overlayUsd !== null)
    heroRows.push({ label: "Off-broker overlay (carried from strike)", usd: overlayUsd });

  const heroRowsSum = heroRows.reduce((s, r) => s + (r.usd || 0), 0);
  if (typeof headlineNav === "number" && Math.abs(headlineNav - heroRowsSum) > Math.max(1000, headlineNav * 0.002)) {
    heroRows.push({ label: "Other / unmodeled", usd: (headlineNav as number) - heroRowsSum });
  }
  const heroComponentsLabel = heroRows
    .map((r) =>
      r.label.startsWith("Fixed")
        ? "FI"
        : r.label.startsWith("Cash")
        ? "cash"
        : r.label.startsWith("IBKR")
        ? "cash"
        : r.label.startsWith("Bank")
        ? "bank"
        : r.label.startsWith("Accruals")
        ? "accruals"
        : r.label.startsWith("Off-broker")
        ? "overlay"
        : r.label.startsWith("Other")
        ? "other"
        : "LEAPS"
    )
    .join(" + ");
  const navSourceLine =
    reconciledNav !== null
      ? (!overlayReliable
          ? `Estimated now = IBKR book + carried strike overlay (bank & accruals pending administrator close)${
              navDaily?.last_official_date ? ` · official struck ${navDaily.last_official_date}` : ""
            }`
          : `Estimated now = IBKR book + off-broker bank cash + accruals${
              navDaily?.last_official_date ? ` · official struck ${navDaily.last_official_date}` : ""
            }`)
      : "Broker book (IBKR PortfolioNAV) — administrator reconciliation not loaded";

  // Market-data freshness (option prices/greeks): grace = 2 trading sessions
  // (per-session feed; absorbs normal next-morning publish lag + a holiday).
  const marketDataLabel = sessionAgeLabel(marketDataAsOfMs);
  const marketDataStale = sessionStale(marketDataAsOfMs, 2);
  const marketDataAsOfLocal = marketDataAsOfMs ? fmtLocalDateTime(new Date(marketDataAsOfMs)) : null;

  // Peak-state age. Three states on purpose, and "unknown" is NOT folded into "current":
  //   stale === false -> current      stale === true -> behind      stale == null -> unknown
  // Tolerance is one session: the writer runs at 13:30 MT, so before that hour the newest
  // row is legitimately yesterday's. Two or more sessions behind means the job is not running.
  // Five states, from the backend's `status`. The distinction that matters is between
  // "awaiting_today" (one session old, today's run genuinely not due yet — honest, not
  // alarming) and "overdue" (today's run WAS due and did not write). The first version of
  // this shipped without that split, so a run that died at 13:30 MT read as "current" for
  // the rest of the day. Unknown is never folded into current.
  // Label, suspect flag and line coverage come from the shared helper (lib/peakFreshness),
  // so this page and the Risk page cannot drift apart.
  const { label: peakLabel, suspect: peakSuspect, asOf: peakAsOf } = peakFreshnessView(peakFreshness);

  const harvestReadyCount = homeRunCandidates.length;

  /* ---------------------------- Audit checks ---------------------------- */
  const navAdmin: any = (latestNav as any)?.reconciliation?.administrator ?? null;
  const reconciliationLoaded = (latestNav as any)?.reconciliation != null;
  const integrity = data.integrity ?? null;
  const payloadWarnings = data.warnings ?? [];

  const auditRows: AuditRow[] = [];

  // 1. Is the administrator overlay applied at all? This is the precondition for the
  //    two rows below, and the highest-leverage line on the strip: fetchLatestNav
  //    silently falls back to /get_portfolio_nav_latest, whose body carries no
  //    navReconciliation, so the page reverts to the broker book while a perfectly
  //    plausible NAV keeps rendering.
  auditRows.push({
    id: "recon-loaded",
    label: "NAV basis",
    state: reconciliationLoaded ? "ok" : "warn",
    verdict: reconciliationLoaded ? "Reconciled" : "Broker only",
    detail: reconciliationLoaded
      ? "Administrator overlay applied to the headline NAV."
      : "Administrator reconciliation not loaded - the headline is the IBKR broker book alone.",
  });

  // 2. Administrator cross-check. Four states, mirroring the Risk cockpit's read so the
  //    two pages cannot disagree. Absence is the FAILURE signal, not a neutral state:
  //    the backend swallows every exception in _attach_admin_crosscheck and returns with
  //    no `administrator` key, so "missing" and "broken" look identical from here.
  //    Never reads the top-level reconciliation.break_flag, which is hardcoded false
  //    whenever component NAVs are absent - i.e. on the normal live path.
  if (!reconciliationLoaded || !navAdmin) {
    auditRows.push({
      id: "admin",
      label: "Administrator cross-check",
      state: "warn",
      verdict: "Not run",
      detail:
        "NAV Fund Services has not cross-checked the broker NAV, or the feed failed. Not evidence of agreement.",
    });
  } else if (navAdmin.break_flag === true) {
    auditRows.push({
      id: "admin",
      label: "Administrator cross-check",
      state: "bad",
      verdict: "Break",
      detail: `Administrator vs broker gap beyond tolerance at ${navAdmin.admin_as_of ?? "the last strike"}. Reconcile before quoting NAV.`,
    });
  } else if (navAdmin.bank_cash_reliable === false) {
    auditRows.push({
      id: "admin",
      label: "Administrator cross-check",
      state: "warn",
      verdict: "Pending close",
      detail: `Bank/accruals split withheld pending administrator close (${navAdmin.admin_as_of ?? "-"}). This is NOT a break - the split is unavailable, not wrong.`,
    });
  } else {
    auditRows.push({
      id: "admin",
      label: "Administrator cross-check",
      state: "ok",
      verdict: "Reconciled",
      detail: `Agrees with NAV Fund Services as of ${navAdmin.admin_as_of ?? "the last strike"}.`,
    });
  }

  // 3. Off-broker overlay. What the headline adds on top of the broker book - and the
  //    honest replacement for a "composition ties out" tick, because it names what it
  //    measures: a figure CARRIED from the last monthly strike, not measured today.
  const overlayForAudit =
    typeof navDaily?.admin_overlay_usd === "number" ? (navDaily.admin_overlay_usd as number) : null;
  auditRows.push({
    id: "overlay",
    label: "Off-broker overlay",
    state: !reconciliationLoaded || overlayForAudit === null ? "warn" : overlayReliable ? "ok" : "warn",
    verdict:
      !reconciliationLoaded || overlayForAudit === null
        ? "Unknown"
        : `${dollars(overlayForAudit, 0)}${overlayReliable ? "" : " (est.)"}`,
    detail:
      !reconciliationLoaded || overlayForAudit === null
        ? "Cannot say how far the headline sits above the broker book - administrator reconciliation not loaded."
        : `Official NAV minus broker NAV at the ${navDaily?.last_official_date ?? "last"} strike - carried forward, not measured today.` +
          (overlayReliable ? "" : " Bank/accruals split pending close, so this is the combined overlay."),
  });

  // 4. Greeks snapshot age - OLDEST line, and how many carry no stamp at all.
  const oldestRiskMs = integrity?.oldestRiskMs ?? null;
  const noStamp = integrity?.noRiskStamp ?? 0;
  auditRows.push({
    id: "greeks-age",
    label: "Greeks snapshot",
    state: oldestRiskMs === null ? "warn" : sessionStale(oldestRiskMs, 2) ? "bad" : noStamp > 0 ? "warn" : "ok",
    verdict: oldestRiskMs === null ? "Unknown" : sessionAgeLabel(oldestRiskMs),
    detail:
      oldestRiskMs === null
        ? "No line carries a risk-snapshot time. Greeks and marks below may be from any session."
        : `Oldest line, not the newest - the headline shows the freshest. ${
            noStamp > 0 ? `${noStamp} of ${integrity?.lines ?? 0} lines carry no stamp at all. ` : ""
          }Stale after 2 trading sessions.`,
  });

  // 5. Lines the marks cannot be trusted on.
  const noQuote = integrity?.noTwoSidedQuote ?? 0;
  const noGreeksLines = integrity?.noGreeks ?? 0;
  auditRows.push({
    id: "quotes",
    label: "Quotes & greeks",
    state: noGreeksLines > 0 ? "bad" : noQuote > 0 ? "warn" : "ok",
    verdict: noQuote === 0 && noGreeksLines === 0 ? "Complete" : `${noQuote} / ${noGreeksLines}`,
    detail:
      noQuote === 0 && noGreeksLines === 0
        ? `All ${integrity?.lines ?? 0} lines have a two-sided quote and greeks.`
        : `${noQuote} line(s) lack a two-sided quote; ${noGreeksLines} have no greeks. Not a coverage percentage - a stored mark would make one read ~100%.`,
  });

  // 6. Missing entry prices. Usually zero, and worth showing precisely because a null
  //    entry is coerced to $0 cost in both cost paths, deflating cost basis AND the
  //    funded % measured against the 40% cap. Every error biased toward reassurance.
  const noEntry = integrity?.noEntryPrice ?? 0;
  auditRows.push({
    id: "entry-price",
    label: "Cost basis on file",
    state: noEntry > 0 ? "bad" : "ok",
    verdict: noEntry > 0 ? `${noEntry} missing` : "Complete",
    detail:
      noEntry > 0
        ? `${noEntry} of ${integrity?.lines ?? 0} lines have no entry price (${dollars(
            integrity?.noEntryPriceMv ?? 0,
            0
          )} of market value). Their cost basis counts as $0, which understates funded basis against the cap.`
        : `All ${integrity?.lines ?? 0} lines have an entry price, so cost basis and funded basis are complete.`,
  });

  // 7. Harvest lockout - capital that is NOT deployable. Gated on the policy block:
  //    portfolio_view falls back to {policy:{}, summary:{lockedNotional:0}} on any
  //    exception, which would otherwise render as a confident "$0 locked".
  const hrPolicy: any = (homeRun as any)?.policy ?? null;
  const lockedNotional =
    typeof homeRun?.summary?.lockedNotional === "number" ? homeRun.summary.lockedNotional : null;
  const activeLocks = homeRun?.summary?.activeLockCount ?? 0;
  const policyLoaded = typeof hrPolicy?.enabled === "boolean";
  auditRows.push({
    id: "lockout",
    label: "Harvest lockout",
    state: !policyLoaded ? "warn" : activeLocks > 0 ? "warn" : "ok",
    verdict: !policyLoaded ? "Not loaded" : activeLocks > 0 ? dollars(lockedNotional ?? 0, 0) : "None",
    detail: !policyLoaded
      ? "Lockout policy did not load, so $0 locked cannot be trusted. Treat deployable cash as unconfirmed."
      : activeLocks > 0
      ? `Locked across ${activeLocks} harvest(s); next unlock ${
          homeRun?.summary?.nextUnlockAtUTC ? fmtLocalDateTime(homeRun.summary.nextUnlockAtUTC) : "unavailable"
        }. This cash is not deployable.${
          hrPolicy?.enabled === false || hrPolicy?.apply_to_buy_budget === false
            ? // CORRECTED 2026-09-04. This read "yet this is still subtracted from the buy
              // budget", which inverts the backend: get_locked_redeployment_summary emits
              // `lockedNotional if enabled and apply_to_buy_budget else 0.0`, and that zero is
              // the literal value rebalance_logic subtracts (rebalance_logic.py:947-950). With
              // the policy off the engine subtracts NOTHING. Left uncorrected it would have
              // contradicted the lockout disclosures now shown on Risk, Screener and Signals.
              " Lockout policy is OFF, so the engine does NOT subtract this from the buy budget — the capital is under lock but the buy budget is not reduced."
            : ""
        }`
      : "No capital is under a redeployment lockout.",
  });

  // 8. Build warnings the payload already emits and nothing has ever read.
  auditRows.push({
    id: "warnings",
    label: "Payload build",
    state: payloadWarnings.length > 0 ? "warn" : "ok",
    verdict: payloadWarnings.length > 0 ? `${payloadWarnings.length} warning(s)` : "Clean",
    detail:
      payloadWarnings.length > 0
        ? `Stages that reported a problem building this payload: ${payloadWarnings.join(
            "; "
          )}. Figures above may be incomplete.`
        : "get_portfolio_view built every section without a warning.",
  });

  /* ------------------------- Owed action today -------------------------
   * Replaces the four-item "needs attention" checklist. Same inputs, three rule
   * changes that are the whole point:
   *   - counts and verdicts only, never a dollar or a percentage (those live on the
   *     page that owns them, which each row now names);
   *   - every row says WHERE it is handled, because this page cannot action most of it;
   *   - rows that are clear collapse into one line, so a quiet day reads as quiet
   *     rather than as four boxes of green.
   * The staged-ticket count deliberately links to the Trade Desk, whose query is
   * unlimited; this page's own pendingOrders is a SELECT TOP 50 and undercounts
   * above that. */
  const owedRows: OwedActionRow[] = [
    {
      id: "market-data",
      label: "Market data freshness",
      verdict: marketDataStale ? marketDataLabel : "Current",
      tone: marketDataStale ? "danger" : "positive",
      needsAction: marketDataStale,
      handledAt: "Risk",
      href: "/risk",
      detail: marketDataStale
        ? "Prices and greeks have not refreshed this session, so market value and returns below may be from a prior session."
        : "Option prices and greeks are current for this session.",
    },
    {
      id: "allocation",
      label: "Sizing vs target",
      // Plain-English verdicts, because these read in two places: as an open row and,
      // when clear, inline in the resting line. "OVER TARGET" read as a contradiction
      // there ("clear: sizing vs target (over target)") when it is in fact the benign
      // state -- market value drifted up on winners with funded basis under the cap.
      verdict:
        dispActionState === "TRIM"
          ? "Trim"
          : dispActionState === "AT CAP"
          ? "At cap"
          : dispActionState === "ADD"
          ? "Add"
          : unevaluatedOver
          ? "Not evaluated"
          : dispActionState === "OVER TARGET"
          ? "No trim required"
          : "On target",
      tone: dispActionTone === "danger" ? "danger" : dispActionTone === "warning" ? "warning" : "positive",
      needsAction: dispActionState === "TRIM" || dispActionState === "ADD" || dispActionState === "AT CAP",
      handledAt: "Signals",
      href: "/leaps",
      detail:
        executableTrim && trimState.engineStagedTrim
          ? "The rebalance engine has a trim to target for this run. Tickets are advisory; review in the Trade Desk."
          : gateNotConfirmed
          ? "The regime target is below current weight, but the sell gate is not confirmed, so the engine staged no trim."
          : unevaluatedOver
          ? "Over target, but the rebalance engine did not run, so no trim was evaluated. Check Signals after the next engine run."
          : dispActionState === "AT CAP"
          ? "Funded basis is at the deployment cap: no further funded buying. Not a trim signal."
          : dispActionState === "OVER TARGET"
          ? "Market value drifted above target on appreciation. Funded basis is under the cap, so no trim is required."
          : action.reason,
    },
    {
      id: "exits",
      label: "Exit review queue",
      verdict: exitWatchSummary.total > 0 ? `${exitWatchSummary.total} flagged` : "Clear",
      tone: exitWatchSummary.hard > 0 ? "danger" : exitWatchSummary.total > 0 ? "warning" : "positive",
      needsAction: exitWatchSummary.total > 0,
      handledAt: "below",
      detail:
        exitWatchSummary.total > 0
          ? `${exitWatchSummary.hard} hard exit, ${exitWatchSummary.peaked} peaked. Hard-exit is not evaluated by any producer, so its absence is not an all-clear.`
          : "No line is flagged for exit review.",
    },
    {
      id: "harvest",
      label: "Home-run harvest",
      verdict: harvestReadyCount > 0 ? `${harvestReadyCount} ready` : "None",
      tone: harvestReadyCount > 0 ? "info" : "positive",
      needsAction: harvestReadyCount > 0,
      // Handled on Signals since 2026-08-27 (Q7). The count stays here because the
      // audit strip still reports locked capital, which is a book-of-record fact.
      handledAt: "Signals",
      href: "/leaps",
      detail:
        harvestReadyCount > 0
          ? "Lines at or above the 2x threshold with enough quantity to trim and leave a runner."
          : "No line is above the 2x home-run threshold.",
    },
    {
      id: "staged",
      label: "Staged tickets",
      verdict:
        pendingSummary.total > 0
          ? pendingSummary.capped
            ? `${pendingSummary.total}+`
            : String(pendingSummary.total)
          : "None",
      tone: pendingSummary.total > 0 ? "info" : "positive",
      needsAction: pendingSummary.total > 0,
      handledAt: "Trade Desk",
      href: "/trading-desk",
      detail:
        pendingSummary.total > 0
          ? `${pendingSummary.sells} sell, ${pendingSummary.buys} buy.${
              pendingSummary.capped
                ? " This payload is capped at " +
                  PENDING_ORDERS_PAYLOAD_CAP +
                  " tickets, so there may be more - the Trade Desk has the full queue."
                : ""
            } The Trade Desk records fill quantity and price; this page cannot.`
          : "The engines have staged nothing.",
    },
  ];

  // Badges derived from the owed rows, never computed a second time.
  const drawerSpecs = buildDrawerSpecs(PORTFOLIO_SECTIONS, owedRows);
  // Dev-only: shouts in the console if a row is owed on this page with no drawer to
  // open it, or a section points at a row that was renamed away.
  assertDrawerInvariant(owedRows);
  // Plain object, not useMemo: this sits after the component's early returns, so a hook
  // here would be called conditionally. It is a seven-entry map over an array we just
  // built — memoising it would cost more than it saves.
  const specById = Object.fromEntries(drawerSpecs.map((d) => [d.id, d])) as Record<
    string,
    (typeof drawerSpecs)[number]
  >;

  const statusStyle =
    statusType === "success"
      ? badgeStyle("success")
      : statusType === "error"
      ? badgeStyle("danger")
      : badgeStyle("neutral");

  return (
    <Layout>
      <PortfolioMethodologyModal open={methodologyOpen} onClose={() => setMethodologyOpen(false)} />
      <div className="space-y-6 p-4" style={{ backgroundColor: ui.pageBg, color: ui.textPrimary }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: ui.textPrimary }}>
              Portfolio
            </h1>
            <div className="mt-0.5 max-w-3xl text-xs leading-relaxed" style={secondaryText}>
              What the fund owns, what it cost, and proof it ties to NAV. Sizing lives on{" "}
              <Link to="/leaps" style={{ color: ui.accentInfo }} className="underline-offset-2 hover:underline">
                Signals
              </Link>
              ; risk and reconciliation on{" "}
              <Link to="/risk" style={{ color: ui.accentInfo }} className="underline-offset-2 hover:underline">
                Risk
              </Link>
              ; tickets on the{" "}
              <Link to="/trading-desk" style={{ color: ui.accentInfo }} className="underline-offset-2 hover:underline">
                Trade Desk
              </Link>
              .
            </div>
          </div>
          <div className="flex items-center gap-3">
            {statusMsg && (
              <div className="rounded px-3 py-1 text-sm" style={statusStyle}>
                {statusMsg}
              </div>
            )}
            {navNote && (
              <div className="rounded px-3 py-1 text-sm" style={badgeStyle("warning")}>
                {navNote}
              </div>
            )}
            <HelionButton size="sm" onClick={() => setMethodologyOpen(true)}>
              Methodology
            </HelionButton>
            <HelionButton size="sm" onClick={handleRefreshHoldings} disabled={refreshing}>
              {refreshing ? "Refreshing…" : "Refresh holdings"}
            </HelionButton>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1.25fr_1fr]">
          <NavHeroCard
            totalNav={headlineNav}
            rows={heroRows}
            componentsLabel={heroComponentsLabel}
            sourceLine={navSourceLine}
            navAsOf={latestNav?.as_of ?? null}
            navSource={latestNav?.source ?? null}
            marketDataAsOf={marketDataAsOfLocal}
            marketDataLabel={marketDataLabel}
            marketDataStale={marketDataStale}
            peakLabel={peakLabel}
            peakSuspect={peakSuspect}
            peakAsOf={peakAsOf}
            actionState={dispActionState}
            actionTone={dispActionTone}
          />

          {/* The session's NAV path. Until 2026-08-31 this existed only in flight: the
              60-second broadcast pushed a number to the screen and nothing wrote it down,
              while dbo.PortfolioNAV kept one row per day and rewrote it in place. The chart
              breaks its line at gaps rather than drawing across them — see splitSegments in
              NavIntradayChart. Display-only; nothing here trades. */}
          {/* self-start: hug the content height instead of stretching to match the
              (taller) right-hand column — the stretch rendered as a half-empty card. */}
          <Card style={themeCardStyle} className="self-start">
            <CardContent className="space-y-2 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide" style={smallMutedText}>
                  NAV — {navRange === "1d" ? "intraday" : navRange.toUpperCase()}
                </div>
                <div className="flex items-center gap-2 text-[11px]" style={smallMutedText}>
                  {/* Range picker. 1D keeps the day stepper; longer ranges hide it —
                      they are anchored to today by definition. */}
                  <div className="flex items-center gap-1">
                    {["1d", "2d", "5d", "mtd", "qtd", "ytd", "itd"].map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setNavRange(r)}
                        className="rounded px-1.5 py-0.5 uppercase"
                        style={{
                          border: `1px solid ${ui.cardBorder}`,
                          color: navRange === r ? ui.textPrimary : ui.textSecondary,
                          background: navRange === r ? "var(--surface-group)" : "transparent",
                          fontWeight: navRange === r ? 600 : 400,
                        }}
                        aria-pressed={navRange === r}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                  {navRange === "1d" ? (
                    <>
                      <button
                        type="button"
                        onClick={() => stepNavCurveDate(-1)}
                        className="rounded px-1.5 py-0.5"
                        style={{ border: `1px solid ${ui.cardBorder}`, color: ui.textSecondary }}
                        aria-label="Previous day"
                      >
                        &lsaquo;
                      </button>
                      <span style={{ color: ui.textPrimary, fontVariantNumeric: "tabular-nums" }}>
                        {navCurve?.date ?? navCurveDate ?? "—"}
                      </span>
                      <button
                        type="button"
                        onClick={() => stepNavCurveDate(1)}
                        className="rounded px-1.5 py-0.5"
                        style={{ border: `1px solid ${ui.cardBorder}`, color: ui.textSecondary }}
                        aria-label="Next day"
                      >
                        &rsaquo;
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
              {navRange === "1d" ? (
                <NavIntradayChart series={navCurve} loading={navCurveLoading && !navCurve} />
              ) : (
                /* Only hand the chart a series that matches the SELECTED range — after a
                   switch, the cached payload still describes the previous range and would
                   render under the wrong label until the fetch lands. */
                <NavRangeChart
                  series={navRangeSeries?.range === navRange ? navRangeSeries : null}
                  loading={navRangeLoading}
                />
              )}
            </CardContent>
          </Card>
          {/* Total NAV is the canonical reconciled figure (IBKR + NAV admin); LEAPS
              allocation-vs-target lives in the attention panel (broker-book sizing basis). */}
          <div className="flex flex-col gap-4">
            {/* Detect-and-ask confirmations for the flow ledger — renders only when
                something needs an answer; confirming self-heals the tile below. */}
            <LedgerConfirmCard />
            {/* Estimated period return, directly under the NAV hero it is measured
                against. Same shared component as Risk and Signals. */}
            <PeriodReturnTile reconciliation={latestNav?.reconciliation ?? null} />
            <OwedActionPanel rows={owedRows} loading={loading} />
          </div>
        </div>

        <AuditChecksCard rows={auditRows} />

        <SectionRail
          specs={drawerSpecs}
          open={drawers.open}
          onJump={drawers.jumpTo}
          onSetAll={drawers.setAll}
        />

        <SectionDrawer
          spec={specById.positions}
          open={drawers.open.positions}
          onToggle={drawers.toggle}
          summary={`One table, four lenses — all lines, flagged, shortest runway, largest${
            exitHintsLastUpdatedUTC ? ` · exit hints ${exitHintsLastUpdatedUTC}` : ""
          }`}
        >
          <PositionsSection
            rows={data.heldLeaps ?? []}
            summary={exitWatchSummary}
            lastUpdatedUTC={exitHintsLastUpdatedUTC}
          />
        </SectionDrawer>

        <SectionDrawer
          spec={specById.runway}
          open={drawers.open.runway}
          onToggle={drawers.toggle}
          summary="Calendar expiry runway. NOT the surface deployment ladder on Risk, which uses different boundaries on purpose."
        >
          <DteBucketedLeapsSection rows={data.heldLeaps ?? []} columns={heldColumnsFiltered} />
        </SectionDrawer>

        <SectionDrawer
          spec={specById.rebalance}
          open={drawers.open.rebalance}
          onToggle={drawers.toggle}
          summary="Sleeve target vs current, trim preview, and the specific contracts a trim would touch. Sizing is authoritative on Signals."
        >
          <Card style={themeCardStyle}>
            <CardContent className="space-y-3 p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>
                    Allocation & Rebalance
                  </div>
                  <div className="text-xs" style={secondaryText}>
                    Read-only telemetry—used to validate regime shifts and trim sizing.
                  </div>
                </div>
                {reb?.block_reason ? (
                  <div className="rounded border px-2 py-1 text-xs" style={badgeStyle("warning")}>
                    Blocked: <strong>{String(reb.block_reason)}</strong>
                  </div>
                ) : null}
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <SleeveStat label="Options Sleeve" target={sleeveTarget} current={sleeveCurrent} />

                <div className="rounded-lg px-3 py-2 text-sm" style={sectionCardStyle}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium" style={{ color: ui.textPrimary }}>
                      Regime
                    </div>
                    <div style={secondaryText}>
                      <strong style={{ color: ui.textPrimary }}>
                        {reb?.effective_regime
                          ? String(reb.effective_regime)
                          : reb?.regime
                          ? String(reb.regime)
                          : "—"}
                      </strong>
                    </div>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs" style={secondaryText}>
                    <span>Defense confirmed</span>
                    <span className="font-semibold" style={{ color: ui.textPrimary }}>
                      {typeof reb?.defense_confirmed === "boolean"
                        ? reb.defense_confirmed
                          ? "Yes"
                          : "No"
                        : "—"}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs" style={secondaryText}>
                    <span>Target (capped)</span>
                    <span className="font-semibold" style={{ color: ui.textPrimary }}>
                      {typeof rebTargetCapped === "number" ? pct(rebTargetCapped, 2) : "—"}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs" style={secondaryText}>
                    <span>Current weight</span>
                    <span className="font-semibold" style={{ color: ui.textPrimary }}>
                      {typeof rebCurrentW === "number" ? pct(rebCurrentW, 2) : "—"}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs" style={secondaryText} title="Cost-basis / funded exposure. Winners can grow above 40% market value without creating a funded-cap breach.">
                    <span>Funded basis</span>
                    <span className="font-semibold" style={{ color: ui.textPrimary }}>
                      {typeof sleeveFundedCurrent === "number" ? pct(sleeveFundedCurrent, 2) : "—"}
                      {fundedBasisIsEngine ? "" : " (est.)"}
                    </span>
                  </div>
                </div>

                <div className="rounded-lg px-3 py-2 text-sm" style={sectionCardStyle}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium" style={{ color: ui.textPrimary }}>
                      Cash reserve
                    </div>
                    <div style={secondaryText}>
                      <strong style={{ color: ui.textPrimary }}>{dollars(reservedCash)}</strong>
                    </div>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs" style={secondaryText}>
                    <span>Cash % of NAV</span>
                    <span className="font-semibold" style={{ color: ui.textPrimary }}>
                      {typeof cashPctOfNav === "number" ? pct(cashPctOfNav, 2) : "—"}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs" style={secondaryText}>
                    <span>Override</span>
                    <span className="font-semibold" style={{ color: ui.textPrimary }}>
                      {cashOverrideEnabled ? "On" : "Off"}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs" style={secondaryText}>
                    {/* Was "FI active / target" with the 60% target hardcoded as
                        pct(0.60, 0) -- a UI constant standing in for a strategy
                        parameter the payload does not carry, rendered next to a live
                        figure with nothing distinguishing the two. The live share
                        stays; the invented target is gone until the backend publishes
                        one. */}
                    <span>FI share of NAV</span>
                    <span className="font-semibold" style={{ color: ui.textPrimary }}>
                      {typeof fiActualTotalNavPct === "number" ? pct(fiActualTotalNavPct, 2) : "—"}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px]" style={smallMutedText}>
                    {cashOverrideEnabled
                      ? latestNav?.cash_override?.reason || latestNav?.cash_override?.mode || "Observed IBKR cash treated as discretionary reserve."
                      : latestNav?.source
                      ? `Latest NAV source ${String(latestNav.source).toUpperCase()}.`
                      : "Latest NAV source unavailable."}
                  </div>
                </div>

                <div className="rounded-lg px-3 py-2 text-sm" style={sectionCardStyle}>
                  <div className="flex items-center justify-between font-medium" style={{ color: ui.textPrimary }}>
                    <span>Trim preview</span>
                    <span className="text-xs" style={smallMutedText}>
                      not staged by UI
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs" style={secondaryText}>
                    <span>Suggested</span>
                    <span className="font-semibold" style={{ color: ui.textPrimary }}>
                      {trimPreviewTxt}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs" style={secondaryText}>
                    <span>Reason</span>
                    <span className="font-semibold" style={{ color: ui.textPrimary }}>
                      {trimReasonTxt}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs" style={secondaryText}>
                    <span>VIX Z</span>
                    <span className="font-semibold" style={{ color: ui.textPrimary }}>
                      {typeof reb?.vix_zscore === "number" ? reb.vix_zscore.toFixed(2) : "—"}
                    </span>
                  </div>
                </div>
              </div>

              <RebalanceSellPreviewCompact orders={rebOrders} />
            </CardContent>
          </Card>
        </SectionDrawer>

        <SectionDrawer
          spec={specById["book-quality"]}
          open={drawers.open["book-quality"]}
          onToggle={drawers.toggle}
          summary="Cost basis, concentration, quote coverage and posture across the whole book."
        >
          <LeapsRiskSnapshotPanel rows={data.heldLeaps ?? []} />
        </SectionDrawer>

        <SectionDrawer
          spec={specById.tickets}
          open={drawers.open.tickets}
          onToggle={drawers.toggle}
          summary="What the engines have staged. The Trade Desk records fill quantity and price; this page cannot."
        >
          <Card style={themeCardStyle}>
            <CardContent className="space-y-2 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold" style={{ color: ui.textPrimary }}>
                    Staged Orders (PendingOrders)
                  </div>
                  <div className="text-xs" style={secondaryText}>
                    What the engines have staged—Portfolio View does not create orders.
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-xs" style={secondaryText}>
                    Last updated:{" "}
                    <strong style={{ color: ui.textPrimary }}>
                      {pendingSummary.lastUpdatedUTC ?? "—"}
                    </strong>
                  </div>

                  </div>
              </div>

              <div className="flex flex-wrap gap-2 text-xs">
                <span className="rounded px-2 py-0.5" style={{ backgroundColor: ui.chipBg, color: ui.textPrimary }}>
                  Total:{" "}
                  <strong>
                    {pendingSummary.total}
                    {pendingSummary.capped ? "+" : ""}
                  </strong>
                </span>
                <span className="rounded border px-2 py-0.5" style={{ ...inlineBoxStyle, color: ui.textSecondary }}>
                  Sells: <strong style={{ color: ui.textPrimary }}>{pendingSummary.sells}</strong>
                </span>
                <span className="rounded border px-2 py-0.5" style={{ ...inlineBoxStyle, color: ui.textSecondary }}>
                  Buys: <strong style={{ color: ui.textPrimary }}>{pendingSummary.buys}</strong>
                </span>
                {pendingSummary.fi > 0 ? (
                  <span className="rounded border px-2 py-0.5" style={{ ...inlineBoxStyle, color: ui.textSecondary }}>
                    FI-tagged: <strong style={{ color: ui.textPrimary }}>{pendingSummary.fi}</strong>
                  </span>
                ) : null}
                {pendingSummary.topReasons.length > 0 ? (
                  <span className="rounded border px-2 py-0.5" style={{ ...inlineBoxStyle, color: ui.textSecondary }}>
                    Top reasons:{" "}
                    <strong style={{ color: ui.textPrimary }}>
                      {pendingSummary.topReasons.map(([k, v]) => `${k} (${v})`).join(", ")}
                    </strong>
                  </span>
                ) : (
                  <span className="rounded border px-2 py-0.5" style={{ ...inlineBoxStyle, color: ui.textMuted }}>
                    No staged reasons to display.
                  </span>
                )}
              </div>

              <StagedExitOrdersCompact orders={stagedExitOrders} />
            </CardContent>
          </Card>
        </SectionDrawer>
      </div>
    </Layout>
  );
};

export default PortfolioView;